import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3300';

// Export API_BASE_URL for use in other components
export { API_BASE_URL };

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Types
export interface User {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'USER' | 'ADMIN';
  isActive?: boolean;
  trainingCompleted?: boolean;
  preferredLanguage?: 'ARABIC' | 'ENGLISH';
  organizationId?: number; // Multi-tenancy: user's organization
  createdAt?: string;
  updatedAt?: string;
  teamId?: number | null;
  team?: Team | null;
}

export interface Team {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  organizationId: number;
  users?: User[];
  _count?: {
    users: number;
  };
}

export interface CreateTeamDto {
  name: string;
  description?: string;
  isActive?: boolean;
}

export interface AuthResponse {
  access_token: string;
  user: User;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface ChatUserSummary {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'USER' | 'ADMIN';
  isActive?: boolean;
  teamId?: number | null;
  team?: {
    id: number;
    name: string;
  } | null;
}

export interface ChatParticipant {
  id: number;
  userId: number;
  joinedAt: string;
  lastReadMessageId?: number | null;
  lastReadAt?: string | null;
  isMuted: boolean;
  isArchived: boolean;
  user: ChatUserSummary;
}

export interface ChatMessage {
  id: number;
  conversationId: number;
  senderId?: number | null;
  organizationId: number;
  body: string;
  messageType: 'TEXT' | 'SYSTEM';
  createdAt: string;
  updatedAt: string;
  editedAt?: string | null;
  deletedAt?: string | null;
  sender: ChatUserSummary | null;
}

export interface ChatConversation {
  id: number;
  type: 'DIRECT' | 'GROUP';
  directKey?: string | null;
  organizationId: number;
  createdAt: string;
  updatedAt: string;
  lastMessageAt?: string | null;
  participants: ChatParticipant[];
  otherParticipant: ChatUserSummary | null;
  lastMessage: ChatMessage | null;
  unreadCount: number;
  currentUserState: {
    lastReadMessageId?: number | null;
    lastReadAt?: string | null;
    isMuted: boolean;
    isArchived: boolean;
  } | null;
}

export interface ChatMessagesResponse {
  data: ChatMessage[];
  pagination: {
    limit: number;
    hasMore: boolean;
    nextBeforeMessageId: number | null;
  };
}

export interface ChatUnreadCountResponse {
  totalUnreadCount: number;
  conversations: Array<{
    conversationId: number;
    unreadCount: number;
  }>;
}

export type SystemNotificationType = 'CHAT' | 'ORDER' | 'DEAL' | 'INVENTORY' | 'TASK' | 'SYSTEM';
export type SystemNotificationSourceType =
  | 'CHAT_MESSAGE'
  | 'DEAL_ACTIVITY'
  | 'STOCK_ALERT'
  | 'TASK'
  | 'ACTIVITY'
  | 'ORDER'
  | 'SYSTEM';

export interface SystemNotification {
  id: number;
  userId: number;
  organizationId: number;
  type: SystemNotificationType;
  sourceType: SystemNotificationSourceType;
  sourceId?: number | null;
  title: string;
  message: string;
  link?: string | null;
  metadata?: Record<string, any> | null;
  readAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationSummaryResponse {
  notifications: SystemNotification[];
  unreadCount: number;
  total: number;
}

export interface Client {
  id: number;
  name: string;
  type: 'ORGANIZATION' | 'COMPANY' | 'STORE' | 'PERSON' | 'OTHER';
  email?: string;
  additionalEmails?: string[];
  phoneNumbers: string[];
  address?: string;
  importance: 'VERY_LOW' | 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH';
  notes?: string;
  assignedUserId?: number;
  assignedUser?: {
    id: number;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  createdById?: number;
  createdBy?: {
    id: number;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  createdAt: string;
  updatedAt: string;
  _count?: {
    deals: number;
  };
  deals?: Deal[];

  // Phase 1.1: Extended Properties
  lifecycleStage?: 'SUBSCRIBER' | 'LEAD' | 'MQL' | 'SQL' | 'OPPORTUNITY' | 'CUSTOMER' | 'EVANGELIST' | 'OTHER';
  leadStatus?: 'NEW' | 'ATTEMPTING_TO_CONTACT' | 'CONNECTED' | 'OPEN_DEAL' | 'UNQUALIFIED' | 'BAD_TIMING' | 'LOST';
  leadSource?: string;
  industry?: string;
  companySize?: number;
  annualRevenue?: number;
  jobTitle?: string;
  department?: string;
  isDecisionMaker?: boolean;
  lastContactDate?: string;
  nextFollowUpDate?: string;
  leadScore?: number;
  tags?: string[];
  website?: string;
  linkedinUrl?: string;
  twitterUrl?: string;
  facebookUrl?: string;
  preferredContactMethod?: string;
  timeZone?: string;
  languagePreference?: 'ARABIC' | 'ENGLISH';
  doNotContact?: boolean;
  gdprConsent?: boolean;
  gdprConsentDate?: string;
  firstSeenDate?: string;
  lastSeenDate?: string;
  totalInteractions?: number;

  // Phase 1.2: Custom Properties
  customPropertyValues?: CustomPropertyValue[];

  // Pipeline & Stage (for client lifecycle tracking)
  pipelineId?: number;
  stageId?: number;
  pipeline?: Pipeline;
  stage?: PipelineStage;
}

export interface ClientQueryFilters {
  page?: number;
  limit?: number;
  search?: string;
  type?: string;
  importance?: string;
  assignedUserId?: number;
  lifecycleStage?: string;
  leadStatus?: string;
}

export interface ClientsResponse {
  data: Client[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface CustomProperty {
  id: number;
  objectType: string;
  name: string;
  label: string;
  fieldType: 'TEXT' | 'TEXTAREA' | 'NUMBER' | 'DATE' | 'DATETIME' | 'DROPDOWN' | 'MULTI_SELECT' | 'CHECKBOX' | 'EMAIL' | 'PHONE' | 'URL';
  options?: string[];
  isRequired: boolean;
  isActive: boolean;
  order: number;
  group?: string;
  helpText?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CustomPropertyValue {
  id: number;
  propertyId: number;
  property?: CustomProperty;
  clientId?: number;
  value: string;
  createdAt: string;
  updatedAt: string;
}

export interface OrderItem {
  id?: number;
  productId?: number;  // Optional link to Product for analytics
  name: string;
  quantity: number;
  unit?: string;
  price: number;
  sku?: string;
  product?: {
    id: number;
    sku: string;
    name: string;
    unit?: string;
  };
  // Stage 3: Inventory allocation
  allocatedQuantity?: number;
  backorderQuantity?: number;
  warehouseId?: number;
  batchNumber?: string;
  warehouse?: {
    id: number;
    name: string;
    code: string;
  };
  // Stage 4: Picking status per item
  pickedQuantity?: number;
  packedQuantity?: number;
}

export interface InvoiceLineItemDraft {
  description?: string;
  weight?: string;
  unitPrice?: string;
  total?: string;
}

export interface InvoiceCustomization {
  invoiceNumber?: string;
  invoiceDate?: string;
  shipperName?: string;
  shipperAddress?: string;
  shipperEmail?: string;
  shipperWebsite?: string;
  consigneeName?: string;
  consigneeAddress?: string;
  consigneeEmail?: string;
  advisingBank?: string;
  terms?: string;
  incoterms?: string;
  buyerNotice?: string;
  termsAndConditions?: string;
  signedByName?: string;
  signedByTitle?: string;
  watermarkText?: string;
  items?: InvoiceLineItemDraft[];
}

export interface SendInvoiceRequest extends InvoiceCustomization {
  companyId: number;
  to?: string[];
  cc?: string[];
  bcc?: string[];
  subject?: string;
  message?: string;
}

// Order Processing Enums
export type OrderSourceChannel = 'WEBSITE' | 'EMAIL' | 'PHONE' | 'IN_PERSON' | 'MARKETPLACE' | 'EDI' | 'API' | 'REFERRAL' | 'TRADE_SHOW' | 'SOCIAL_MEDIA' | 'OTHER';
export type PaymentType = 'PREPAID' | 'NET_30' | 'NET_60' | 'NET_90' | 'COD' | 'LETTER_OF_CREDIT' | 'WIRE_TRANSFER' | 'CREDIT_CARD' | 'PAYPAL' | 'OTHER';
export type PaymentStatus = 'PENDING' | 'PARTIAL' | 'PAID' | 'OVERDUE' | 'REFUNDED' | 'DISPUTED' | 'CANCELLED';
export type FulfillmentPriority = 'STANDARD' | 'EXPEDITED' | 'RUSH' | 'CRITICAL' | 'HOLD';
export type InventoryCheckStatus = 'PENDING' | 'AVAILABLE' | 'PARTIAL' | 'BACKORDER' | 'PRODUCTION_REQUIRED' | 'NOT_AVAILABLE';
export type PickingStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'PICKED' | 'PACKED' | 'QC_PENDING' | 'QC_PASSED' | 'QC_FAILED' | 'READY_TO_SHIP';
export type PickingTaskStatus = 'TO_PICK' | 'IN_PROGRESS' | 'PICKED' | 'QC_PENDING' | 'QC_PASSED' | 'QC_FAILED' | 'PACKED' | 'CANCELLED';
export type QCCheckType = 'CORRECT_PRODUCTS' | 'SEAL_INTEGRITY' | 'NO_LEAKS' | 'LABEL_ACCURACY' | 'WEIGHT_VERIFIED' | 'PACKAGING_QUALITY' | 'DOCUMENTATION_COMPLETE';
export type ShippingStatus = 'NOT_SHIPPED' | 'LABEL_CREATED' | 'PICKED_UP' | 'IN_TRANSIT' | 'OUT_FOR_DELIVERY' | 'DELIVERED' | 'DELIVERY_ATTEMPTED' | 'RETURNED' | 'EXCEPTION';
export type ShippingCarrier = 'FEDEX' | 'UPS' | 'DHL' | 'USPS' | 'ARAMEX' | 'TNT' | 'LOCAL_COURIER' | 'FREIGHT' | 'CUSTOMER_PICKUP' | 'OTHER';
export type DeliveryConfirmationStatus = 'PENDING' | 'CONFIRMED' | 'DISPUTED' | 'PARTIAL_DELIVERY';
export type OrderIssueType = 'DAMAGE' | 'SHORTAGE' | 'WRONG_ITEM' | 'QUALITY_ISSUE' | 'CUSTOMS_DELAY' | 'LOST_SHIPMENT' | 'PAYMENT_DISPUTE' | 'RETURN_REQUEST' | 'OTHER';
export type OrderIssueStatus = 'OPEN' | 'INVESTIGATING' | 'RESOLVED' | 'ESCALATED' | 'CLOSED';
export type OrderClosureStatus = 'OPEN' | 'PENDING_CLOSURE' | 'CLOSED' | 'ARCHIVED';

export interface OrderIssue {
  id: number;
  dealId: number;
  issueType: OrderIssueType;
  status: OrderIssueStatus;
  priority: FulfillmentPriority;
  description: string;
  resolution?: string;
  reportedAt: string;
  resolvedAt?: string;
  reportedBy?: number;
  resolvedBy?: number;
  refundAmount?: number;
  notes?: string;
}

export interface OrderDocument {
  id: number;
  dealId: number;
  documentType: DocumentType;
  name: string;
  description?: string;
  filePath: string;
  fileSize?: number;
  mimeType?: string;
  uploadedAt: string;
  uploadedBy?: number;
  isArchived: boolean;
  archivedAt?: string;
  products?: Array<{ id: number; sku: string; name: string }>;
}

export interface CustomerPurchaseOrder extends OrderDocument {
  uploadedByUser?: {
    id: number;
    email: string;
    firstName?: string;
    lastName?: string;
  } | null;
  deal?: {
    id: number;
    orderNumber?: string;
    description?: string;
    client?: {
      id: number;
      name: string;
    } | null;
  } | null;
  products: Array<{ id: number; sku: string; name: string }>;
}

// Picking Task Interfaces
export interface PickingTaskItem {
  id: number;
  pickingTaskId: number;
  orderItemId?: number;
  productId: number;
  inventoryItemId?: number;
  warehouseCode?: string;
  shelfCode?: string;
  binCode?: string;
  trayCode?: string;
  batchNumber?: string;
  sku?: string;
  productName: string;
  requiredQuantity: number;
  pickedQuantity: number;
  packedQuantity: number;
  routeOrder: number;
  confirmedAt?: string;
  confirmedBy?: number;
  scannedBatchCode?: string;
  product?: { id: number; sku: string; name: string; unit?: string };
  confirmedByUser?: { id: number; firstName: string; lastName: string };
  inventoryItem?: any;
}

export interface QCCheckRecord {
  id: number;
  pickingTaskId: number;
  checkType: QCCheckType;
  passed: boolean;
  notes?: string;
  checkedAt: string;
  checkedBy?: number;
  user?: { id: number; firstName: string; lastName: string };
}

export interface PickingTask {
  id: number;
  dealId: number;
  warehouseId?: number;
  assignedUserId?: number;
  status: PickingTaskStatus;
  startedAt?: string;
  pickedAt?: string;
  qcStartedAt?: string;
  qcCompletedAt?: string;
  packedAt?: string;
  qcPerformedBy?: number;
  qcNotes?: string;
  qcPassed?: boolean;
  packedBy?: number;
  totalWeight?: number;
  totalPackages?: number;
  packagingNotes?: string;
  createdAt: string;
  updatedAt: string;
  deal?: { id: number; orderNumber?: string; description?: string; client?: { id: number; name: string; email?: string } };
  warehouse?: { id: number; name: string; code: string };
  assignedUser?: { id: number; firstName: string; lastName: string; email?: string };
  qcUser?: { id: number; firstName: string; lastName: string };
  packer?: { id: number; firstName: string; lastName: string };
  items: PickingTaskItem[];
  qcChecks: QCCheckRecord[];
  _count?: { items: number; qcChecks: number };
}

export interface PackingSlipData {
  taskId: number;
  orderNumber?: string;
  client?: { id: number; name: string; email?: string };
  packedAt?: string;
  packedBy?: { id: number; firstName: string; lastName: string };
  totalWeight?: number;
  totalPackages?: number;
  packagingNotes?: string;
  items: {
    productName: string;
    sku?: string;
    batchNumber?: string;
    quantity: number;
    location: string;
  }[];
  qcChecks: QCCheckRecord[];
  qcPassed?: boolean;
  qcNotes?: string;
}

export interface Deal {
  id: number;
  clientId: number;
  userId: number;
  amount: string;
  currency: string;
  description: string;
  details?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  orderItems?: OrderItem[];
  client?: {
    id: number;
    name: string;
    type: string;
    email?: string;
    additionalEmails?: string[];
    phoneNumbers?: string[];
    address?: string;
  };
  user?: {
    id: number;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  // Phase 1: Pipeline fields
  pipelineId?: number;
  stageId?: number;
  probability?: number;
  closeDate?: string;
  forecastCategory?: 'PIPELINE' | 'BEST_CASE' | 'COMMIT' | 'OMITTED';
  pipeline?: Pipeline;
  stage?: PipelineStage;

  // ============================================
  // ORDER PROCESSING & FULFILLMENT FIELDS
  // ============================================

  // Stage 1: Order Receipt
  orderNumber?: string;
  sourceChannel?: OrderSourceChannel;
  paymentType?: PaymentType;
  paymentStatus?: PaymentStatus;
  paymentDueDate?: string;
  paymentReceivedDate?: string;
  poNumber?: string;
  moqVerified?: boolean;

  // Stage 2: Order Entry & Logging
  fulfillmentPriority?: FulfillmentPriority;
  targetShipDate?: string;
  specialInstructions?: string;
  internalNotes?: string;

  // Stage 3: Inventory & Production Check
  inventoryStatus?: InventoryCheckStatus;
  inventoryCheckedAt?: string;
  inventoryCheckedBy?: number;
  backorderDate?: string;
  productionRequired?: boolean;
  productionNotes?: string;

  // Stage 4: Picking & Packing
  pickingStatus?: PickingStatus;
  pickedAt?: string;
  pickedBy?: number;
  packedAt?: string;
  packedBy?: number;
  qcPassedAt?: string;
  qcPassedBy?: number;
  qcNotes?: string;
  totalWeight?: number;
  totalPackages?: number;

  // Stage 5: Shipping
  shippingStatus?: ShippingStatus;
  shippingCarrier?: ShippingCarrier;
  trackingNumber?: string;
  trackingUrl?: string;
  shippedAt?: string;
  shippedBy?: number;
  estimatedDelivery?: string;
  shippingCost?: number;
  shippingMethod?: string;
  isInternational?: boolean;
  customsValue?: number;
  customsDescription?: string;
  hsCode?: string;

  // Stage 6: Post-Shipping & Delivery
  deliveredAt?: string;
  deliveryConfirmation?: DeliveryConfirmationStatus;
  deliveryConfirmedAt?: string;
  deliverySignature?: string;
  deliveryNotes?: string;
  trackingEmailSent?: boolean;
  deliveryConfirmationSent?: boolean;

  // Stage 7: Problem Resolution
  hasIssues?: boolean;
  issueType?: OrderIssueType;
  issueStatus?: OrderIssueStatus;
  issueDescription?: string;
  issueReportedAt?: string;
  issueResolvedAt?: string;
  issueResolution?: string;
  refundAmount?: number;
  refundedAt?: string;

  // Stage 8: Order Closure & Archiving
  closureStatus?: OrderClosureStatus;
  closedAt?: string;
  closedBy?: number;
  archivedAt?: string;
  customerFeedback?: string;
  customerRating?: number;

  // Relations
  orderIssues?: OrderIssue[];
  orderDocuments?: OrderDocument[];
}

// Phase 1: Pipeline Types
export interface Pipeline {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
  isDefault: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
  stages?: PipelineStage[];
  _count?: {
    deals: number;
  };
}

export interface PipelineStage {
  id: number;
  pipelineId: number;
  name: string;
  description?: string;
  order: number;
  defaultProbability: number;
  isClosedWon: boolean;
  isClosedLost: boolean;
  color?: string;
  createdAt: string;
  updatedAt: string;
  pipeline?: Pipeline;
  // Stage-to-lifecycle mappings (auto-update client when moved to this stage)
  mappedLifecycleStage?: 'SUBSCRIBER' | 'LEAD' | 'MQL' | 'SQL' | 'OPPORTUNITY' | 'CUSTOMER' | 'EVANGELIST' | 'OTHER';
  mappedLeadStatus?: 'NEW' | 'ATTEMPTING_TO_CONTACT' | 'CONNECTED' | 'OPEN_DEAL' | 'UNQUALIFIED' | 'BAD_TIMING' | 'LOST';
  _count?: {
    deals: number;
  };
}

export interface DealStageHistory {
  id: number;
  dealId: number;
  fromStageId?: number;
  toStageId: number;
  changedById: number;
  notes?: string;
  createdAt: string;
  fromStage?: PipelineStage;
  toStage: PipelineStage;
  changedBy: {
    id: number;
    email: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface PipelineBoardData {
  [stageId: string]: {
    stage: PipelineStage | null;
    deals: Deal[];
  };
}

// Deal Activity Log Types
export type DealActivityAction =
  | 'CREATED' | 'UPDATED' | 'STATUS_CHANGED' | 'STAGE_CHANGED'
  | 'PAYMENT_STATUS_CHANGED' | 'SHIPPING_STATUS_CHANGED'
  | 'INVENTORY_ALLOCATED' | 'INVENTORY_DEDUCTED' | 'INVOICE_GENERATED' | 'SHIPMENT_PROCESSED'
  | 'PARTIAL_SHIPMENT' | 'ISSUE_CREATED' | 'ISSUE_RESOLVED'
  | 'DOCUMENT_UPLOADED' | 'NOTE_ADDED' | 'ITEM_ADDED' | 'ITEM_REMOVED' | 'ITEM_UPDATED';

export interface DealActivityLog {
  id: number;
  dealId: number;
  action: DealActivityAction;
  description: string;
  fieldName?: string;
  oldValue?: string;
  newValue?: string;
  metadata?: Record<string, any>;
  userId?: number;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
  deal?: {
    id: number;
    orderNumber?: string;
    description: string;
    client?: { id: number; name: string };
  };
  user?: {
    id: number;
    firstName?: string;
    lastName?: string;
    email: string;
  };
}

// Stock Alert Types
export type StockAlertType = 'LOW_STOCK' | 'OUT_OF_STOCK' | 'BACKORDER_NEEDED' | 'REORDER_POINT' | 'STOCK_RESERVED';
export type StockAlertStatus = 'ACTIVE' | 'ACKNOWLEDGED' | 'RESOLVED' | 'DISMISSED';

export interface StockAlert {
  id: number;
  productId: number;
  alertType: StockAlertType;
  status: StockAlertStatus;
  message: string;
  currentQty: number;
  requiredQty?: number;
  threshold?: number;
  dealId?: number;
  resolvedAt?: string;
  resolvedBy?: number;
  createdAt: string;
  updatedAt: string;
  product?: {
    id: number;
    name: string;
    sku: string;
  };
  deal?: {
    id: number;
    orderNumber?: string;
    description?: string;
  };
}

export interface ClientStageHistory {
  id: number;
  clientId: number;
  fromStageId?: number;
  toStageId: number;
  changedById: number;
  notes?: string;
  createdAt: string;
  fromStage?: PipelineStage;
  toStage: PipelineStage;
  changedBy: {
    id: number;
    email: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface ForecastData {
  summary: {
    totalDeals: number;
    totalValue: number;
    weightedValue: number;
  };
  byStage: {
    [stageId: string]: {
      stage: PipelineStage;
      count: number;
      totalValue: number;
      weightedValue: number;
      deals: Deal[];
    };
  };
  byUser: {
    [userId: string]: {
      user: User;
      count: number;
      totalValue: number;
      weightedValue: number;
    };
  };
  byCategory: {
    [category: string]: {
      category: string;
      count: number;
      totalValue: number;
      weightedValue: number;
    };
  };
}

// Phase 2: Quote & Pricing Types
export type QuoteStatus = 'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED';

export interface QuoteItem {
  id: number;
  quoteId: number;
  productId?: number;
  name: string;
  description?: string;
  sku?: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  discountAmount: number;
  discountPercent: number;
  subtotal: number;
  isOptional: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
  product?: Product;
}

export interface QuoteAcceptance {
  id: number;
  quoteId: number;
  acceptedAt: string;
  acceptedByName: string;
  acceptedByEmail?: string;
  acceptedByIp?: string;
  signatureType: 'typed' | 'drawn' | 'uploaded';
  signatureData?: string;
  comments?: string;
}

export interface Quote {
  id: number;
  dealId?: number | null;
  clientId: number;
  companyId: number;
  quoteNumber: string;
  revisionNumber: number;
  parentQuoteId?: number;
  status: QuoteStatus;

  // Amounts
  subtotal: number;
  discountAmount: number;
  discountPercent: number;
  taxAmount: number;
  taxPercent: number;
  total: number;
  currency: string;

  // Validity
  validFrom: string;
  validUntil?: string;
  expiresAt?: string;

  // Content
  title?: string;
  introduction?: string;
  terms?: string;
  notes?: string;

  // Tracking
  sentAt?: string;
  viewedAt?: string;
  viewCount: number;

  // Public access
  publicToken?: string;

  createdById: number;
  createdAt: string;
  updatedAt: string;

  // Relations
  deal?: Deal;
  client?: {
    id: number;
    name: string;
    type?: string;
    email?: string;
    additionalEmails?: string[];
    phoneNumbers?: string[];
  };
  company?: {
    id: number;
    name: string;
  };
  parentQuote?: Quote;
  childQuotes?: Quote[];
  createdBy?: {
    id: number;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  items?: QuoteItem[];
  acceptance?: QuoteAcceptance;
}

export interface CreateQuoteItemDto {
  productId?: number;
  name: string;
  description?: string;
  sku?: string;
  quantity: number;
  unit?: string;
  unitPrice: number;
  discountAmount?: number;
  discountPercent?: number;
  isOptional?: boolean;
  order?: number;
}

export interface CreateQuoteDto {
  companyId: number;
  dealId?: number;
  clientId?: number;
  title?: string;
  introduction?: string;
  terms?: string;
  notes?: string;
  currency?: string;
  discountPercent?: number;
  taxPercent?: number;
  validUntil?: string;
  expiresAt?: string;
  items: CreateQuoteItemDto[];
}

export interface UpdateQuoteDto extends Partial<CreateQuoteDto> {
  status?: QuoteStatus;
}

export interface AcceptQuoteDto {
  acceptedByName: string;
  acceptedByEmail?: string;
  signatureType: 'typed' | 'drawn' | 'uploaded';
  signatureData?: string;
  comments?: string;
}

// Phase 2: Activity Types
export type ActivityType = 'CALL' | 'EMAIL' | 'MEETING' | 'NOTE' | 'TASK' | 'SMS' | 'WHATSAPP' | 'LINKEDIN_MESSAGE' | 'OTHER';
export type ActivityOutcome = 'SUCCESSFUL' | 'NO_ANSWER' | 'LEFT_VOICEMAIL' | 'BUSY' | 'WRONG_NUMBER' | 'SCHEDULED_FOLLOWUP' | 'NOT_INTERESTED' | 'INTERESTED' | 'MEETING_SCHEDULED' | 'DEAL_CLOSED' | 'DEAL_LOST' | 'COMPLETED' | 'CANCELLED' | 'OTHER';
export type TaskStatus = 'TODO' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'OVERDUE';
export type TaskPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

export interface Activity {
  id: number;
  type: ActivityType;
  subject: string;
  description?: string;
  outcome?: ActivityOutcome;
  duration?: number; // Duration in minutes

  // Task-specific fields
  taskStatus?: TaskStatus;
  taskPriority?: TaskPriority;
  dueDate?: string;
  completedAt?: string;

  // Email-specific fields
  emailTo?: string;
  emailFrom?: string;
  emailCc?: string;
  emailSubject?: string;

  // Meeting-specific fields
  meetingLocation?: string;
  meetingStartTime?: string;
  meetingEndTime?: string;
  attendees?: string[];

  // General metadata
  isFollowUpRequired: boolean;
  followUpDate?: string;
  tags: string[];
  attachments: string[];

  createdAt: string;
  updatedAt: string;

  // Relations
  clientId: number;
  client?: {
    id: number;
    name: string;
    type: string;
  };

  userId?: number;
  user?: {
    id: number;
    email: string;
    firstName?: string;
    lastName?: string;
  };

  dealId?: number;
  deal?: {
    id: number;
    description: string;
    amount: string;
  };
}

export interface SupplierContact {
  id: number;
  supplierId: number;
  name: string;
  role: string;
  email?: string;
  phone?: string;
  createdAt: string;
  updatedAt: string;
}

export type CatalogueProductType =
  | 'PRODUCT'
  | 'RAW_MATERIAL'
  | 'SPICES'
  | 'HERBS'
  | 'OILS_AND_OIL_SEEDS'
  | 'COSMETIC_AND_NATURAL_BEAUTY'
  | 'DRIED_FOOD'
  | 'SEEDS'
  | 'EXTRACTS'
  | 'HERBAL_INFUSIONS';
export type CatalogueProductCategory = 'OIL' | 'ACTIVE' | 'PACKAGING' | 'LABEL' | 'FINISHED_PRODUCT';

export interface SupplierProductType {
  id: number;
  name: string;
  legacyKey?: CatalogueProductType | null;
  description?: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SupplierCatalogueProduct {
  id: number;
  supplierId: number;
  supplier?: {
    id: number;
    name: string;
  };
  type?: CatalogueProductType | null;
  typeId?: number | null;
  productType?: SupplierProductType | null;
  sku?: string;
  name: string;
  categoryId?: number;
  category?: SupplierProductCategory | null;
  cost?: string;
  leadTime?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SupplierProductListResponse {
  data: SupplierCatalogueProduct[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface SupplierProductImportBatchResult {
  importedCount: number;
  skippedCount: number;
  skipped: Array<{ rowNumber: number; reason: string }>;
  replayed: boolean;
}

const normalizeSupplierProductListResponse = (
  payload: any,
  filters?: { page?: number; limit?: number },
): SupplierProductListResponse => {
  const requestedPage = Math.max(filters?.page || 1, 1);
  const requestedLimit = Math.min(Math.max(filters?.limit || 25, 1), 100);
  const paginationSource = payload?.pagination || payload?.meta || payload?.data?.pagination;
  const rawProducts = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.data?.data)
        ? payload.data.data
        : Array.isArray(payload?.products)
          ? payload.products
          : [];
  const hasServerPagination = Boolean(paginationSource);
  const total = Number(paginationSource?.total ?? rawProducts.length);
  const page = Number(paginationSource?.page ?? requestedPage);
  const limit = Number(paginationSource?.limit ?? requestedLimit);
  const pages = Number(
    paginationSource?.pages ??
    paginationSource?.totalPages ??
    (limit > 0 ? Math.ceil(total / limit) : 0)
  );
  const start = (requestedPage - 1) * requestedLimit;

  return {
    data: hasServerPagination ? rawProducts : rawProducts.slice(start, start + requestedLimit),
    pagination: {
      page,
      limit,
      total,
      pages,
    },
  };
};

export type SupplierStatus = 'ACTIVE_SUPPLIER' | 'POTENTIAL_SUPPLIER';

export type SupplierType = 'MANUFACTURER' | 'DISTRIBUTOR' | 'WHOLESALER' | 'SERVICE_PROVIDER' | 'RAW_MATERIAL_SUPPLIER' | 'PACKAGING_SUPPLIER' | 'CONTRACT_MANUFACTURER' | 'OIL_SUPPLIER' | 'OTHER' | 'SUPPLIER' | 'RAW_MATERIALS_SOURCING';

export interface Supplier {
  id: number;
  name: string;
  type: SupplierType;
  types?: SupplierType[];
  customType?: string;
  status: SupplierStatus;
  emails: string[];
  phoneNumbers: string[];
  address?: string;
  websites: string[];
  importance: 'VERY_LOW' | 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH';
  notes?: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    deals: number;
    contacts: number;
    catalogueProducts: number;
  };
  deals?: SupplierDeal[];
  contacts?: SupplierContact[];
  catalogueProducts?: SupplierCatalogueProduct[];
}

export interface SupplierExportRow {
  name: string;
  type: SupplierType;
  types?: SupplierType[];
  customType?: string | null;
  emails: string[];
  phoneNumbers: string[];
  importance: Supplier['importance'];
  _count: {
    deals: number;
  };
}

export interface SupplierExportResponse {
  data: SupplierExportRow[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface SupplierQueryFilters {
  page?: number;
  limit?: number;
  search?: string;
  productSearch?: string;
  type?: string;
  importance?: string;
  status?: string;
  nameSort?: 'asc' | 'desc';
}

export interface SupplierDeal {
  id: number;
  supplierId: number;
  amount: string;
  currency: string;
  description: string;
  details?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  supplier?: {
    id: number;
    name: string;
    type: string;
    email?: string;
    phoneNumbers?: string[];
    address?: string;
  };
}

export type SampleStatus =
  | 'REQUESTED'
  | 'PENDING'
  | 'SHIPPED'
  | 'DELIVERED'
  | 'UNDER_REVIEW'
  | 'APPROVED'
  | 'REJECTED'
  | 'RETURNED'
  | 'LOST'
  | 'EXPIRED';

export type SampleType =
  | 'FREE_SAMPLE'
  | 'PAID_SAMPLE'
  | 'TRIAL'
  | 'DEMO'
  | 'REPLACEMENT'
  | 'QUALITY_CHECK';

export interface Sample {
  id: number;

  // Core Tracking
  trackingNumber?: string | null;
  lotNumber?: string | null;
  sampleCode?: string;

  // Client & Relationships
  clientId: number;
  client?: {
    id: number;
    name: string;
    email?: string;
    type: string;
  };

  // Quote & Supplier References
  customerQuoteRef?: string;
  originalQuoteRef?: string;
  supplierId?: number;
  supplier?: {
    id: number;
    name: string;
    email?: string;
  };
  supplierOfferId?: number;
  supplierOffer?: {
    id: number;
    productName: string;
    unitPrice: string;
    totalPrice: string;
  };

  // Product Information
  productId?: number;
  product?: {
    id: number;
    name: string;
    sku: string;
    description?: string;
  };
  productName: string;
  productSku?: string;
  quantity: string;
  unit: string;

  // Sample Details
  sampleType: SampleType;
  status: SampleStatus;
  batchNumber?: string;
  manufacturingDate?: string;
  expiryDate?: string;

  // Cost Tracking
  sampleCost?: string;
  shippingCost?: string;
  totalCost?: string;
  currency: string;

  // Shipping Information
  shippingCarrier?: string;
  shippingMethod?: string;
  shippingDate?: string;
  estimatedDeliveryDate?: string;
  actualDeliveryDate?: string;
  deliveryRecipient?: string;
  deliverySignature?: string;

  // Return Information
  isReturnable: boolean;
  returnDate?: string;
  returnReason?: string;
  returnCondition?: string;

  // Follow-up & Feedback
  followUpDate?: string;
  clientFeedback?: string;
  clientRating?: number;
  clientDecision?: string;
  feedbackDate?: string;

  // Conversion Tracking
  convertedToDeal: boolean;
  dealId?: number;
  deal?: {
    id: number;
    description: string;
    amount: string;
  };

  // Documentation
  notes?: string;
  specialInstructions?: string;
  attachments: string[];

  // Metadata
  createdById?: number;
  createdBy?: {
    id: number;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  assignedUserId?: number;
  assignedUser?: {
    id: number;
    email: string;
    firstName?: string;
    lastName?: string;
  };

  createdAt: string;
  updatedAt: string;
}

// Auth API
export const authAPI = {
  login: async (email: string, password: string): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  register: async (userData: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
    role?: 'USER' | 'ADMIN';
    preferredLanguage?: 'ARABIC' | 'ENGLISH';
    teamId?: number | null;
  }): Promise<AuthResponse> => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  getProfile: async (): Promise<{ user: User }> => {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
  },
};

// Teams API
export const teamsAPI = {
  getAll: async (): Promise<Team[]> => {
    const response = await api.get('/teams');
    return response.data;
  },

  getById: async (id: number): Promise<Team> => {
    const response = await api.get(`/teams/${id}`);
    return response.data;
  },

  create: async (teamData: CreateTeamDto): Promise<Team> => {
    const response = await api.post('/teams', teamData);
    return response.data;
  },

  update: async (id: number, teamData: Partial<CreateTeamDto>): Promise<Team> => {
    const response = await api.patch(`/teams/${id}`, teamData);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/teams/${id}`);
  },

  addMember: async (teamId: number, userId: number): Promise<void> => {
    await api.post(`/teams/${teamId}/members`, { userId });
  },

  removeMember: async (teamId: number, userId: number): Promise<void> => {
    await api.delete(`/teams/${teamId}/members/${userId}`);
  },
};

// Clients API
export const clientsAPI = {
  search: async (query?: string): Promise<Client[]> => {
    const response = await api.get('/clients/search', { params: { q: query } });
    return response.data;
  },

  getAll: async (filters?: ClientQueryFilters): Promise<ClientsResponse> => {
    const response = await api.get('/clients', { params: filters });
    return response.data;
  },

  getById: async (id: number): Promise<Client> => {
    const response = await api.get(`/clients/${id}`);
    return response.data;
  },

  create: async (clientData: Omit<Client, 'id' | 'createdAt' | 'updatedAt' | 'assignedUser' | '_count' | 'deals'>): Promise<Client> => {
    const response = await api.post('/clients', clientData);
    return response.data;
  },

  update: async (id: number, clientData: Partial<Omit<Client, 'id' | 'createdAt' | 'updatedAt' | 'assignedUser' | '_count' | 'deals'>>): Promise<Client> => {
    const response = await api.patch(`/clients/${id}`, clientData);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/clients/${id}`);
  },

  getDeals: async (id: number): Promise<Deal[]> => {
    const response = await api.get(`/clients/${id}/deals`);
    return response.data;
  },

  // Admin-only assignment functions
  assignClient: async (clientId: number, userId: number): Promise<Client> => {
    const response = await api.post(`/clients/${clientId}/assign`, { userId });
    return response.data;
  },

  unassignClient: async (clientId: number): Promise<Client> => {
    const response = await api.post(`/clients/${clientId}/unassign`);
    return response.data;
  },

  // Client stage management
  moveStage: async (clientId: number, toStageId: number, notes?: string): Promise<Client> => {
    const response = await api.patch(`/clients/${clientId}/stage`, { toStageId, notes });
    return response.data;
  },

  getStageHistory: async (clientId: number): Promise<ClientStageHistory[]> => {
    const response = await api.get(`/clients/${clientId}/stage-history`);
    return response.data;
  },

  // Client segment management
  getSegments: async (clientId: number): Promise<Segment[]> => {
    const response = await api.get(`/clients/${clientId}/segments`);
    return response.data;
  },

  addToSegments: async (clientId: number, segmentIds: number[]): Promise<Segment[]> => {
    const response = await api.post(`/clients/${clientId}/segments`, { segmentIds });
    return response.data;
  },

  removeFromSegments: async (clientId: number, segmentIds: number[]): Promise<Segment[]> => {
    const response = await api.delete(`/clients/${clientId}/segments`, { data: { segmentIds } });
    return response.data;
  },

  // Soft-Delete folder
  getDeleted: async (): Promise<any[]> => {
    const response = await api.get('/clients/deleted');
    return response.data;
  },

  restore: async (id: number): Promise<Client> => {
    const response = await api.patch(`/clients/${id}/restore`);
    return response.data;
  },
};

// Deals API
export const dealsAPI = {
  getAll: async (params?: any): Promise<Deal[]> => {
    const response = await api.get('/deals', { params });
    // Handle paginated response structure
    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    return Array.isArray(response.data) ? response.data : [];
  },

  getAllPaginated: async (params?: any): Promise<{ data: Deal[]; pagination: any }> => {
    const response = await api.get('/deals', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Deal> => {
    const response = await api.get(`/deals/${id}`);
    return response.data;
  },

  create: async (dealData: {
    clientId: number;
    amount?: number;
    currency?: string;
    description: string;
    details?: string;
    notes?: string;
    orderItems?: OrderItem[];
  }): Promise<Deal> => {
    const response = await api.post('/deals', dealData);
    return response.data;
  },

  update: async (id: number, dealData: Partial<{
    clientId: number;
    amount: number;
    currency: string;
    description: string;
    details: string;
    notes: string;
    orderItems: OrderItem[];
  }>): Promise<Deal> => {
    const response = await api.patch(`/deals/${id}`, dealData);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/deals/${id}`);
  },

  downloadInvoice: async (dealId: number, companyId: number, customization?: InvoiceCustomization): Promise<void> => {
    const response = customization
      ? await api.post(`/deals/${dealId}/invoice`, { ...customization, companyId }, { responseType: 'blob' })
      : await api.get(`/deals/${dealId}/invoice`, {
        params: { companyId },
        responseType: 'blob',
      });

    // Create a download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `invoice-${dealId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  sendInvoice: async (dealId: number, data: SendInvoiceRequest): Promise<{ sent: boolean; recipients: string[] }> => {
    const response = await api.post(`/deals/${dealId}/invoice/send`, data);
    return response.data;
  },

  uploadDocument: async (
    dealId: number,
    file: File,
    options?: { documentType?: DocumentType; name?: string; description?: string }
  ): Promise<OrderDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', options?.documentType || 'PURCHASE_ORDER');
    if (options?.name) formData.append('name', options.name);
    if (options?.description) formData.append('description', options.description);

    const response = await api.post(`/deals/${dealId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getDocuments: async (dealId: number): Promise<OrderDocument[]> => {
    const response = await api.get(`/deals/${dealId}/documents`);
    return response.data;
  },

  downloadDocument: async (dealId: number, documentId: number, fileName: string): Promise<void> => {
    const response = await api.get(`/deals/${dealId}/documents/${documentId}/download`, {
      responseType: 'blob',
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  deleteDocument: async (dealId: number, documentId: number): Promise<void> => {
    await api.delete(`/deals/${dealId}/documents/${documentId}`);
  },

  downloadReceipt: async (dealId: number, companyId: number): Promise<void> => {
    const response = await api.get(`/deals/${dealId}/receipt`, {
      params: { companyId },
      responseType: 'blob',
    });

    // Create a download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `receipt-${dealId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Activity Logs
  getActivityLogs: async (dealId: number, options?: { limit?: number; offset?: number }): Promise<{ logs: DealActivityLog[]; total: number }> => {
    const response = await api.get(`/deals/${dealId}/activity-logs`, { params: options });
    return response.data;
  },

  getAllActivityLogs: async (options?: { limit?: number; offset?: number; action?: string }): Promise<{ logs: DealActivityLog[]; total: number }> => {
    const response = await api.get('/deals/activity-logs/all', { params: options });
    return response.data;
  },

  // Shipping
  processShipment: async (dealId: number, data: {
    trackingNumber: string;
    shippingCarrier: ShippingCarrier;
    shippingMethod?: string;
    isInternational?: boolean;
  }): Promise<{ deal: Deal; inventoryDeductions: any; isPartialShipment: boolean }> => {
    const response = await api.post(`/deals/${dealId}/ship`, data);
    return response.data;
  },

  updateTracking: async (dealId: number, data: {
    trackingNumber: string;
    shippingCarrier: ShippingCarrier;
    shippingMethod?: string;
    isInternational?: boolean;
  }): Promise<Deal> => {
    const response = await api.patch(`/deals/${dealId}/tracking`, data);
    return response.data;
  },

  // Order Issues/Problems
  createIssue: async (dealId: number, data: {
    issueType: OrderIssueType;
    description: string;
    priority?: FulfillmentPriority;
    notes?: string;
    refundAmount?: number;
  }): Promise<OrderIssue> => {
    const response = await api.post(`/deals/${dealId}/issues`, data);
    return response.data;
  },

  getIssues: async (dealId: number): Promise<OrderIssue[]> => {
    const response = await api.get(`/deals/${dealId}/issues`);
    return response.data;
  },

  updateIssue: async (dealId: number, issueId: number, data: {
    issueType?: OrderIssueType;
    description?: string;
    priority?: FulfillmentPriority;
    status?: OrderIssueStatus;
    notes?: string;
    refundAmount?: number;
    resolution?: string;
  }): Promise<OrderIssue> => {
    const response = await api.patch(`/deals/${dealId}/issues/${issueId}`, data);
    return response.data;
  },

  resolveIssue: async (dealId: number, issueId: number, data: {
    resolution: string;
    refundAmount?: number;
  }): Promise<OrderIssue> => {
    const response = await api.post(`/deals/${dealId}/issues/${issueId}/resolve`, data);
    return response.data;
  },

  deleteIssue: async (dealId: number, issueId: number): Promise<void> => {
    await api.delete(`/deals/${dealId}/issues/${issueId}`);
  },
};

// Quotes API
export const quotesAPI = {
  getAll: async (filters?: { dealId?: number; clientId?: number; status?: QuoteStatus }): Promise<Quote[]> => {
    const response = await api.get('/quotes', { params: filters });
    return response.data;
  },

  getById: async (id: number): Promise<Quote> => {
    const response = await api.get(`/quotes/${id}`);
    return response.data;
  },

  getByPublicToken: async (token: string): Promise<Quote> => {
    const response = await api.get(`/quotes/public/${token}`);
    return response.data;
  },

  create: async (quoteData: CreateQuoteDto): Promise<Quote> => {
    const response = await api.post('/quotes', quoteData);
    return response.data;
  },

  update: async (id: number, quoteData: UpdateQuoteDto): Promise<Quote> => {
    const response = await api.patch(`/quotes/${id}`, quoteData);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/quotes/${id}`);
  },

  createRevision: async (id: number): Promise<Quote> => {
    const response = await api.post(`/quotes/${id}/revisions`);
    return response.data;
  },

  send: async (id: number, data?: { to: string[] }): Promise<Quote> => {
    const response = await api.post(`/quotes/${id}/send`, data || {});
    return response.data;
  },

  accept: async (id: number, acceptanceData: AcceptQuoteDto): Promise<Quote> => {
    const response = await api.post(`/quotes/${id}/accept`, acceptanceData);
    return response.data;
  },

  downloadPdf: async (id: number): Promise<void> => {
    const response = await api.get(`/quotes/${id}/pdf`, {
      responseType: 'blob',
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `quote-${id}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  getPublicUrl: (token: string): string => {
    return `${API_BASE_URL}/quotes/public/${token}`;
  },
};

// Users API
export const usersAPI = {
  getAll: async (): Promise<User[]> => {
    const response = await api.get('/users');
    return response.data;
  },

  getSalesmen: async (): Promise<User[]> => {
    const response = await api.get('/users/salesmen');
    return response.data;
  },

  getById: async (id: number): Promise<User> => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  update: async (id: number, data: Partial<User>): Promise<User> => {
    const response = await api.patch(`/users/${id}`, data);
    return response.data;
  },

  resetPassword: async (id: number, password: string): Promise<User> => {
    const response = await api.patch(`/users/${id}/reset-password`, { password });
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/users/${id}`);
  },
};

// Chat API
export const chatAPI = {
  getConversations: async (): Promise<ChatConversation[]> => {
    const response = await api.get('/chat/conversations');
    return response.data;
  },

  createDirectConversation: async (targetUserId: number): Promise<ChatConversation> => {
    const response = await api.post('/chat/conversations/direct', { targetUserId });
    return response.data;
  },

  getConversationMessages: async (
    conversationId: number,
    params?: { beforeMessageId?: number; limit?: number },
  ): Promise<ChatMessagesResponse> => {
    const response = await api.get(`/chat/conversations/${conversationId}/messages`, { params });
    return response.data;
  },

  sendMessage: async (conversationId: number, body: string): Promise<ChatMessage> => {
    const response = await api.post(`/chat/conversations/${conversationId}/messages`, { body });
    return response.data;
  },

  markConversationRead: async (
    conversationId: number,
    messageId?: number,
  ): Promise<{
    conversationId: number;
    lastReadMessageId?: number | null;
    lastReadAt?: string | null;
    unreadCount: number;
  }> => {
    const response = await api.post(`/chat/conversations/${conversationId}/read`, {
      messageId,
    });
    return response.data;
  },

  getAvailableUsers: async (params?: {
    search?: string;
    limit?: number;
  }): Promise<ChatUserSummary[]> => {
    const response = await api.get('/chat/users/available', { params });
    return response.data;
  },

  getUnreadCount: async (): Promise<ChatUnreadCountResponse> => {
    const response = await api.get('/chat/unread-count');
    return response.data;
  },
};

export const customerPurchaseOrdersAPI = {
  getAll: async (filters?: {
    search?: string;
    clientId?: number;
    productId?: number;
    uploadedByMe?: boolean;
  }): Promise<CustomerPurchaseOrder[]> => {
    const response = await api.get('/customer-purchase-orders', { params: filters });
    return response.data;
  },

  download: async (purchaseOrder: CustomerPurchaseOrder): Promise<void> => {
    const response = await api.get(`/deals/${purchaseOrder.dealId}/documents/${purchaseOrder.id}/download`, {
      responseType: 'blob',
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', purchaseOrder.name);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

// Notifications API
export const notificationsAPI = {
  getSummary: async (params?: { limit?: number }): Promise<NotificationSummaryResponse> => {
    const response = await api.get('/notifications/summary', { params });
    return response.data;
  },

  markRead: async (id: number): Promise<SystemNotification> => {
    const response = await api.post(`/notifications/${id}/read`);
    return response.data;
  },

  markAllRead: async (): Promise<{ updatedCount: number }> => {
    const response = await api.post('/notifications/read-all');
    return response.data;
  },
};

// Analytics API (Admin only)
export const analyticsAPI = {
  getOverview: async () => {
    const response = await api.get('/analytics/overview');
    return response.data;
  },

  getSalesmanPerformance: async (period?: string, startDate?: string, endDate?: string) => {
    const response = await api.get('/analytics/salesman-performance', {
      params: { period, startDate, endDate },
    });
    return response.data;
  },

  getDealsByPeriod: async (period?: string, startDate?: string, endDate?: string) => {
    const response = await api.get('/analytics/deals-by-period', {
      params: { period, startDate, endDate },
    });
    return response.data;
  },

  getClientsByPeriod: async (period?: string, startDate?: string, endDate?: string) => {
    const response = await api.get('/analytics/clients-by-period', {
      params: { period, startDate, endDate },
    });
    return response.data;
  },

  getRevenueTrends: async (period?: string, startDate?: string, endDate?: string) => {
    const response = await api.get('/analytics/revenue-trends', {
      params: { period, startDate, endDate },
    });
    return response.data;
  },

  getTopPerformers: async (period?: string, startDate?: string, endDate?: string) => {
    const response = await api.get('/analytics/top-performers', {
      params: { period, startDate, endDate },
    });
    return response.data;
  },

  getActivitySummary: async () => {
    const response = await api.get('/analytics/activity-summary');
    return response.data;
  },

  getClientsBySalesman: async (period?: string, startDate?: string, endDate?: string) => {
    const response = await api.get('/analytics/clients-by-salesman', {
      params: { period, startDate, endDate },
    });
    return response.data;
  },

  getStockAnalytics: async (period?: string, startDate?: string, endDate?: string) => {
    const response = await api.get('/analytics/stock-analytics', {
      params: { period, startDate, endDate },
    });
    return response.data;
  },

  getProductAnalytics: async (period?: string, startDate?: string, endDate?: string) => {
    const response = await api.get('/analytics/product-analytics', {
      params: { period, startDate, endDate },
    });
    return response.data;
  },

  getSupplierAnalytics: async (period?: string, startDate?: string, endDate?: string) => {
    const response = await api.get('/analytics/supplier-analytics', {
      params: { period, startDate, endDate },
    });
    return response.data;
  },

  getQuickMetrics: async (period?: string, startDate?: string, endDate?: string) => {
    const response = await api.get('/analytics/quick-metrics', {
      params: { period, startDate, endDate },
    });
    return response.data;
  },

  getTeamPerformanceMetrics: async (period?: string, startDate?: string, endDate?: string) => {
    const response = await api.get('/analytics/team-performance-metrics', {
      params: { period, startDate, endDate },
    });
    return response.data;
  },

  getClientAcquisitionFunnel: async (period?: string, startDate?: string, endDate?: string) => {
    const response = await api.get('/analytics/client-acquisition-funnel', {
      params: { period, startDate, endDate },
    });
    return response.data;
  },

  getProductPerformanceList: async (period?: string, startDate?: string, endDate?: string) => {
    const response = await api.get('/analytics/product-performance-list', {
      params: { period, startDate, endDate },
    });
    return response.data;
  },

  getClientList: async (period?: string, startDate?: string, endDate?: string) => {
    const response = await api.get('/analytics/client-list', {
      params: { period, startDate, endDate },
    });
    return response.data;
  },

  getAdvancedChartData: async (
    chartType?: string,
    xAxisType?: string,
    yAxisMetric?: string,
    period?: string,
    startDate?: string,
    endDate?: string
  ) => {
    const response = await api.get('/analytics/advanced-chart-data', {
      params: { chartType, xAxisType, yAxisMetric, period, startDate, endDate },
    });
    return response.data;
  },

  getClientAnalytics: async (period?: string, startDate?: string, endDate?: string) => {
    const response = await api.get('/analytics/client-analytics', {
      params: { period, startDate, endDate },
    });
    return response.data;
  },
};

// Video types
export interface TrainingVideo {
  id: number;
  title: string;
  description?: string;
  filename: string;
  filePath: string;
  duration?: number;
  order: number;
  isRequired: boolean;
  isActive: boolean;
  language: 'ARABIC' | 'ENGLISH';
  createdAt: string;
  updatedAt: string;
}

export interface VideoProgress {
  id: number;
  userId: number;
  videoId: number;
  isCompleted: boolean;
  watchedAt?: string;
  createdAt: string;
  updatedAt: string;
  video?: TrainingVideo;
}

export interface TrainingStats {
  totalVideos: number;
  totalUsers: number;
  completedUsers: number;
  completionRate: number;
  userProgress: Array<{
    id: number;
    email: string;
    firstName?: string;
    lastName?: string;
    trainingCompleted: boolean;
    completedVideos: number;
    completedRequiredVideos: number;
  }>;
}

// Videos API
export const videosAPI = {
  // Admin endpoints
  upload: async (formData: FormData): Promise<TrainingVideo> => {
    const response = await api.post('/videos/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getAllForAdmin: async (): Promise<TrainingVideo[]> => {
    const response = await api.get('/videos/admin/all');
    return response.data;
  },

  getStats: async (): Promise<TrainingStats> => {
    const response = await api.get('/videos/admin/stats');
    return response.data;
  },

  update: async (id: number, data: Partial<TrainingVideo>): Promise<TrainingVideo> => {
    const response = await api.patch(`/videos/admin/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/videos/admin/${id}`);
  },

  // User endpoints
  getAll: async (): Promise<TrainingVideo[]> => {
    const response = await api.get('/videos');
    return response.data;
  },

  getMyProgress: async (): Promise<VideoProgress[]> => {
    const response = await api.get('/videos/my-progress');
    return response.data;
  },

  updateProgress: async (videoId: number, isCompleted: boolean): Promise<VideoProgress> => {
    const response = await api.patch(`/videos/${videoId}/progress`, { isCompleted });
    return response.data;
  },

  getTrainingStatus: async (): Promise<{ trainingCompleted: boolean }> => {
    const response = await api.get('/videos/training-status');
    return response.data;
  },

  getStreamUrl: (videoId: number): string => {
    return `${API_BASE_URL}/videos/stream/${videoId}`;
  },
};

export interface SupplierProductCategory {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Suppliers API
export const suppliersAPI = {
  getCategories: async (): Promise<SupplierProductCategory[]> => {
    const response = await api.get('/suppliers/categories');
    return response.data;
  },

  getProductTypes: async (includeInactive: boolean = false): Promise<SupplierProductType[]> => {
    const response = await api.get('/suppliers/product-types', {
      params: includeInactive ? { includeInactive: true } : undefined,
    });
    return response.data;
  },

  createProductType: async (data: { name: string; description?: string; sortOrder?: number }): Promise<SupplierProductType> => {
    const response = await api.post('/suppliers/product-types', data);
    return response.data;
  },

  updateProductType: async (id: number, data: { name?: string; description?: string; sortOrder?: number }): Promise<SupplierProductType> => {
    const response = await api.patch(`/suppliers/product-types/${id}`, data);
    return response.data;
  },

  deleteProductType: async (id: number): Promise<any> => {
    const response = await api.delete(`/suppliers/product-types/${id}`);
    return response.data;
  },

  createCategory: async (data: { name: string; description?: string }): Promise<SupplierProductCategory> => {
    const response = await api.post('/suppliers/categories', data);
    return response.data;
  },

  updateCategory: async (id: number, data: { name?: string; description?: string }): Promise<SupplierProductCategory> => {
    const response = await api.patch(`/suppliers/categories/${id}`, data);
    return response.data;
  },

  deleteCategory: async (id: number): Promise<void> => {
    await api.delete(`/suppliers/categories/${id}`);
  },

  getAllProducts: async (filters?: {
    supplierId?: number;
    categoryId?: number;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<SupplierProductListResponse> => {
    const response = await api.get('/suppliers/products', { params: filters });
    return normalizeSupplierProductListResponse(response.data, filters);
  },

  bulkImportProducts: async (
    supplierId: number,
    products: any[],
    importBatch?: { importId: string; batchIndex: number },
  ): Promise<SupplierProductImportBatchResult> => {
    const response = await api.post(`/suppliers/${supplierId}/catalogue-products/bulk-import`, {
      products,
      ...importBatch,
    });
    return response.data;
  },


  getAll: async (filters?: SupplierQueryFilters): Promise<any> => {
    const response = await api.get('/suppliers', { params: filters });
    return response.data;
  },

  getExportPage: async (filters: SupplierQueryFilters): Promise<SupplierExportResponse> => {
    const response = await api.get('/suppliers/export', { params: filters });
    return response.data;
  },

  getById: async (id: number): Promise<Supplier> => {
    const response = await api.get(`/suppliers/${id}`);
    return response.data;
  },

  create: async (supplierData: any): Promise<Supplier> => {
    const response = await api.post('/suppliers', supplierData);
    return response.data;
  },

  bulkImport: async (suppliers: any[]): Promise<any> => {
    const response = await api.post('/suppliers/bulk-import', { suppliers });
    return response.data;
  },

  update: async (id: number, supplierData: any): Promise<Supplier> => {
    const response = await api.patch(`/suppliers/${id}`, supplierData);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/suppliers/${id}`);
  },

  getDeals: async (id: number): Promise<SupplierDeal[]> => {
    const response = await api.get(`/suppliers/${id}/deals`);
    return response.data;
  },

  // Soft-Delete folder
  getDeleted: async (): Promise<any[]> => {
    const response = await api.get('/suppliers/deleted');
    return response.data;
  },

  restore: async (id: number): Promise<Supplier> => {
    const response = await api.patch(`/suppliers/${id}/restore`);
    return response.data;
  },
};

// Supplier Deals API
export const supplierDealsAPI = {
  getAll: async (supplierId?: number): Promise<SupplierDeal[]> => {
    const response = await api.get('/suppliers/deals/all', { params: { supplierId } });
    return response.data;
  },

  getById: async (id: number): Promise<SupplierDeal> => {
    const response = await api.get(`/suppliers/deals/${id}`);
    return response.data;
  },

  create: async (dealData: {
    supplierId: number;
    amount: number;
    currency?: string;
    description: string;
    details?: string;
    notes?: string;
  }): Promise<SupplierDeal> => {
    const response = await api.post('/suppliers/deals', dealData);
    return response.data;
  },

  update: async (id: number, dealData: Partial<{
    supplierId: number;
    amount: number;
    currency: string;
    description: string;
    details: string;
    notes: string;
  }>): Promise<SupplierDeal> => {
    const response = await api.patch(`/suppliers/deals/${id}`, dealData);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/suppliers/deals/${id}`);
  },
};

// Company types
export interface Company {
  id: number;
  name: string;
  logo?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  taxId?: string;
  registrationNo?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Companies API
export const companiesAPI = {
  getAll: async (): Promise<Company[]> => {
    const response = await api.get('/companies');
    return response.data;
  },

  getOne: async (id: number): Promise<Company> => {
    const response = await api.get(`/companies/${id}`);
    return response.data;
  },

  create: async (companyData: {
    name: string;
    address?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    phone?: string;
    email?: string;
    website?: string;
    taxId?: string;
    registrationNo?: string;
    isActive?: boolean;
  }): Promise<Company> => {
    const response = await api.post('/companies', companyData);
    return response.data;
  },

  update: async (id: number, companyData: Partial<{
    name: string;
    address?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    phone?: string;
    email?: string;
    website?: string;
    taxId?: string;
    registrationNo?: string;
    isActive?: boolean;
  }>): Promise<Company> => {
    const response = await api.patch(`/companies/${id}`, companyData);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/companies/${id}`);
  },

  uploadLogo: async (id: number, file: File): Promise<Company> => {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await api.post(`/companies/${id}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Inventory Types
export interface Warehouse {
  id: number;
  name: string;
  code: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  capacity?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    shelves: number;
  };
  shelves?: Shelf[];
}

export interface Shelf {
  id: number;
  warehouseId: number;
  code: string;
  description?: string;
  capacity?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  warehouse?: Warehouse;
  _count?: {
    bins: number;
  };
  bins?: Bin[];
}

export interface Bin {
  id: number;
  shelfId: number;
  code: string;
  description?: string;
  capacity?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  shelf?: Shelf;
  _count?: {
    trays: number;
  };
  trays?: Tray[];
}

export interface Tray {
  id: number;
  binId: number;
  code: string;
  description?: string;
  capacity?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  bin?: Bin;
  _count?: {
    inventoryItems: number;
  };
  inventoryItems?: InventoryItem[];
}

export interface ProductQuotedPriceReference {
  id: number;
  quoteId: number;
  quoteNumber: string;
  quoteTitle?: string | null;
  quoteStatus: QuoteStatus;
  quoteCreatedAt: string;
  quoteValidUntil?: string | null;
  dealId?: number | null;
  deal?: {
    id: number;
    description: string;
    orderNumber?: string;
  } | null;
  client: {
    id: number;
    name: string;
    type?: string;
  } | null;
  salesman?: {
    id: number;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  quantity: number;
  unit: string;
  unitPrice: number;
  discountAmount: number;
  discountPercent: number;
  subtotal: number;
  effectiveUnitPrice: number;
  currency: string;
}

export interface Product {
  id: number;
  sku: string;
  name: string;
  description?: string;
  supplierId?: number;
  categoryId?: number;
  unit?: string;
  isActive: boolean;
  isInventoryLinked?: boolean;
  createdAt: string;
  updatedAt: string;
  supplier?: Supplier;
  category?: Category;
  _count?: {
    inventoryItems: number;
  };
  inventoryItems?: InventoryItem[];
  documents?: ProductDocument[];
  costRanges?: CostRange[];
  stockSummary?: ProductStockSummary;
  lotSummaries?: LotSummary[];
  customerPurchaseOrders?: CustomerPurchaseOrder[];
  lastQuotedPrice?: ProductQuotedPriceReference | null;
}

export interface InventoryItem {
  id: number;
  productId: number;
  trayId?: number | null;
  lotId?: number | null;
  quantity: number;
  unit?: string | null;
  unitCost?: number | string | null;
  costCurrency?: string | null;
  status: 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK' | 'RESERVED' | 'DAMAGED';
  batchNumber?: string;
  expiryDate?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  product?: Product;
  tray?: Tray;
  lot?: InventoryLot | null;
}

export interface InventoryItemInput {
  productId: number;
  trayId?: number | null;
  quantity: number;
  unit?: string;
  unitCost?: number;
  costCurrency?: string;
  status?: InventoryItem['status'];
  lotId?: number | null;
  batchNumber?: string | null;
  expiryDate?: string | null;
  notes?: string | null;
}

export interface SupplierOffer {
  id: number;
  supplierId: number;
  productName: string;
  description?: string;
  quantity: number;
  unitPrice: string;
  totalPrice: string;
  currency: string;
  validFrom: string;
  validUntil?: string;
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED';
  notes?: string;
  createdAt: string;
  updatedAt: string;
  supplier?: Supplier;
  documents?: OfferDocument[];
}

export type DocumentType = 'SPECIFICATION' | 'CERTIFICATE' | 'DATASHEET' | 'INVOICE' | 'PURCHASE_ORDER' | 'CONTRACT' | 'IMAGE' | 'COA' | 'MSDS' | 'OTHER';

export type InventoryAvailabilityStatus = 'AVAILABLE' | 'PARTIAL' | 'OUT_OF_STOCK' | 'NOT_TRACKED';
export type ProductStockStatus = 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK' | 'NOT_TRACKED';

export interface ProductStockSummary {
  productId: number;
  isInventoryLinked: boolean;
  availableQuantity: number;
  totalQuantity: number;
  stockStatus: ProductStockStatus;
  lotCount?: number;
}

export interface LotSummary {
  lotId?: number | null;
  lotNumber: string;
  supplier?: Pick<Supplier, 'id' | 'name'> | null;
  receivedDate?: string | null;
  expiryDate?: string | null;
  isActive: boolean;
  totalQuantity: number;
  availableQuantity: number;
  itemCount: number;
  locations: string[];
}

export interface InventoryLot {
  id: number;
  lotNumber: string;
  supplierId?: number | null;
  supplier?: Pick<Supplier, 'id' | 'name'> | null;
  receivedDate?: string | null;
  expiryDate?: string | null;
  notes?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  itemCount?: number;
  productCount?: number;
  totalQuantity?: number;
  availableQuantity?: number;
  products?: Array<{
    productId: number;
    sku: string;
    name: string;
    unit?: string;
    totalQuantity: number;
    availableQuantity: number;
  }>;
  inventoryItems?: Array<InventoryItem & {
    lotNumber?: string;
    location?: string;
    warehouseId?: number;
    warehouseName?: string;
  }>;
}

export interface ProductStockInventoryItem {
  id: number;
  productId: number;
  lotId?: number | null;
  quantity: number;
  unit?: string | null;
  status: InventoryItem['status'];
  lotNumber?: string | null;
  lot?: InventoryLot | null;
  batchNumber?: string | null;
  expiryDate?: string;
  notes?: string;
  warehouseId?: number;
  warehouseName?: string;
  location: string;
  createdAt: string;
  updatedAt: string;
}

export interface BlacklistEntry {
  clientId: number;
  name: string;
  lastDealAt: string | null;
  daysSinceLastDeal: number | null;
  eligible: boolean;
  availability: 'AVAILABLE' | 'PROTECTED' | 'PERMANENT';
  contact: {
    email?: string | null;
    additionalEmails: string[];
    phoneNumbers: string[];
    preferredContactMethod?: string | null;
  } | null;
  claimState: 'PENDING' | 'PENDING_BY_YOU' | 'APPROVED' | 'REJECTED' | null;
}

export interface PermanentBlacklistEntry {
  id: number;
  name: string;
  permanentProtectionReason?: string | null;
  permanentlyProtectedAt: string;
  permanentlyProtectedBy?: Pick<User, 'id' | 'email' | 'firstName' | 'lastName'> | null;
}

export interface ClientClaim {
  id: number;
  clientId: number;
  requestedById: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reviewNote?: string | null;
  reviewedAt?: string | null;
  createdAt: string;
  client: { id: number; name: string; deals?: Array<{ createdAt: string }> };
  requestedBy?: Pick<User, 'id' | 'email' | 'firstName' | 'lastName'>;
}

export interface FormulaIngredient {
  id?: number;
  name: string;
  quantity: number | string;
  unit: string;
  notes?: string | null;
  displayOrder?: number;
}

export interface Formula {
  id: number;
  name: string;
  productId?: number | null;
  productReference?: string | null;
  version: string;
  preparationNotes?: string | null;
  product?: { id: number; name: string; sku: string } | null;
  ingredients: FormulaIngredient[];
  createdAt: string;
  updatedAt: string;
}

export interface FormulaInput {
  name: string;
  productId?: number | null;
  productReference?: string | null;
  version?: string;
  preparationNotes?: string | null;
  ingredients: Array<{ name: string; quantity: number; unit: string; notes?: string }>;
}

export interface ProductStockRow {
  productId: number;
  product: Pick<Product, 'id' | 'sku' | 'name' | 'description' | 'unit' | 'isActive' | 'isInventoryLinked'> & {
    category?: Category;
    supplier?: Pick<Supplier, 'id' | 'name'>;
  };
  sku: string;
  name: string;
  unit?: string;
  category?: Category;
  supplier?: Pick<Supplier, 'id' | 'name'>;
  isActive: boolean;
  isInventoryLinked: boolean;
  availableQuantity: number;
  totalQuantity: number;
  stockStatus: ProductStockStatus;
  lotNumbers: string[];
  lotSummaries?: LotSummary[];
  inventoryItems: ProductStockInventoryItem[];
}

export interface OfferDocument {
  id: number;
  supplierOfferId: number;
  fileName: string;
  originalName: string;
  fileUrl: string;
  fileType: DocumentType;
  fileSize: number;
  mimeType: string;
  uploadedAt: string;
}

export interface ProductDocument {
  id: number;
  productId: number;
  parentDocumentId?: number | null;
  fileName: string;
  originalName: string;
  fileUrl: string;
  fileType: DocumentType;
  fileSize: number;
  mimeType: string;
  uploadedAt: string;
  childDocuments?: ProductDocument[];
}

export interface ProductQuoteHistoryItem {
  id: number;
  quoteId: number;
  quoteNumber: string;
  quoteTitle?: string;
  quoteStatus: QuoteStatus;
  quoteCreatedAt: string;
  quoteValidUntil?: string;
  dealId?: number | null;
  deal?: {
    id: number;
    description: string;
    orderNumber?: string;
  } | null;
  client: {
    id: number;
    name: string;
    type?: string;
  };
  salesman?: {
    id: number;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  quantity: number;
  unit: string;
  unitPrice: number;
  discountAmount: number;
  discountPercent: number;
  subtotal: number;
  effectiveUnitPrice: number;
  currency: string;
}

export interface ProductQuoteHistory {
  product: {
    id: number;
    sku: string;
    name: string;
  };
  summary: {
    count: number;
    byCurrency: Array<{
      currency: string;
      count: number;
      minUnitPrice: number;
      maxUnitPrice: number;
      averageUnitPrice: number;
    }>;
  };
  items: ProductQuoteHistoryItem[];
}

// Warehouses API
export const warehousesAPI = {
  getAll: async (): Promise<Warehouse[]> => {
    const response = await api.get('/inventory/warehouses');
    return response.data;
  },
  getOne: async (id: number): Promise<Warehouse> => {
    const response = await api.get(`/inventory/warehouses/${id}`);
    return response.data;
  },
  create: async (data: any): Promise<Warehouse> => {
    const response = await api.post('/inventory/warehouses', data);
    return response.data;
  },
  update: async (id: number, data: any): Promise<Warehouse> => {
    const response = await api.patch(`/inventory/warehouses/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/inventory/warehouses/${id}`);
  },
};

// Shelves API
export const shelvesAPI = {
  getAll: async (warehouseId?: number): Promise<Shelf[]> => {
    const params = warehouseId ? { warehouseId } : {};
    const response = await api.get('/inventory/shelves', { params });
    return response.data;
  },
  getOne: async (id: number): Promise<Shelf> => {
    const response = await api.get(`/inventory/shelves/${id}`);
    return response.data;
  },
  create: async (data: any): Promise<Shelf> => {
    const response = await api.post('/inventory/shelves', data);
    return response.data;
  },
  update: async (id: number, data: any): Promise<Shelf> => {
    const response = await api.patch(`/inventory/shelves/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/inventory/shelves/${id}`);
  },
};

// Bins API
export const binsAPI = {
  getAll: async (shelfId?: number): Promise<Bin[]> => {
    const params = shelfId ? { shelfId } : {};
    const response = await api.get('/inventory/bins', { params });
    return response.data;
  },
  getOne: async (id: number): Promise<Bin> => {
    const response = await api.get(`/inventory/bins/${id}`);
    return response.data;
  },
  create: async (data: any): Promise<Bin> => {
    const response = await api.post('/inventory/bins', data);
    return response.data;
  },
  update: async (id: number, data: any): Promise<Bin> => {
    const response = await api.patch(`/inventory/bins/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/inventory/bins/${id}`);
  },
};

// Trays API
export const traysAPI = {
  getAll: async (binId?: number): Promise<Tray[]> => {
    const params = binId ? { binId } : {};
    const response = await api.get('/inventory/trays', { params });
    return response.data;
  },
  getOne: async (id: number): Promise<Tray> => {
    const response = await api.get(`/inventory/trays/${id}`);
    return response.data;
  },
  create: async (data: any): Promise<Tray> => {
    const response = await api.post('/inventory/trays', data);
    return response.data;
  },
  update: async (id: number, data: any): Promise<Tray> => {
    const response = await api.patch(`/inventory/trays/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/inventory/trays/${id}`);
  },
};

// Products API
export const productsAPI = {
  getAll: async (filters?: { search?: string; category?: string; supplierId?: number }): Promise<Product[]> => {
    const response = await api.get('/inventory/products', { params: filters });
    return response.data;
  },
  getOne: async (id: number): Promise<Product> => {
    const response = await api.get(`/inventory/products/${id}`);
    return response.data;
  },
  create: async (data: any): Promise<Product> => {
    const response = await api.post('/inventory/products', data);
    return response.data;
  },
  update: async (id: number, data: any): Promise<Product> => {
    const response = await api.patch(`/inventory/products/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/inventory/products/${id}`);
  },
  uploadDocument: async (
    productId: number,
    file: File,
    fileType: DocumentType,
    fileName?: string,
    parentDocumentId?: number
  ): Promise<ProductDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileType', fileType);
    if (fileName) {
      formData.append('fileName', fileName);
    }
    if (parentDocumentId !== undefined) {
      formData.append('parentDocumentId', parentDocumentId.toString());
    }
    const response = await api.post(`/inventory/products/${productId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  getDocuments: async (productId: number): Promise<ProductDocument[]> => {
    const response = await api.get(`/inventory/products/${productId}/documents`);
    return response.data;
  },
  deleteDocument: async (documentId: number): Promise<void> => {
    await api.delete(`/inventory/products/documents/${documentId}`);
  },
};

// Inventory Items API
export const inventoryItemsAPI = {
  getAll: async (filters?: { productId?: number; trayId?: number; status?: string }): Promise<InventoryItem[]> => {
    const response = await api.get('/inventory/items', { params: filters });
    return response.data;
  },
  getOne: async (id: number): Promise<InventoryItem> => {
    const response = await api.get(`/inventory/items/${id}`);
    return response.data;
  },
  create: async (data: InventoryItemInput): Promise<InventoryItem> => {
    const response = await api.post('/inventory/items', data);
    return response.data;
  },
  update: async (id: number, data: Partial<InventoryItemInput>): Promise<InventoryItem> => {
    const response = await api.patch(`/inventory/items/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/inventory/items/${id}`);
  },
};

// Supplier Offers API
export const supplierOffersAPI = {
  getAll: async (filters?: { supplierId?: number; status?: string }): Promise<SupplierOffer[]> => {
    const response = await api.get('/inventory/offers', { params: filters });
    return response.data;
  },
  getOne: async (id: number): Promise<SupplierOffer> => {
    const response = await api.get(`/inventory/offers/${id}`);
    return response.data;
  },
  create: async (data: any): Promise<SupplierOffer> => {
    const response = await api.post('/inventory/offers', data);
    return response.data;
  },
  update: async (id: number, data: any): Promise<SupplierOffer> => {
    const response = await api.patch(`/inventory/offers/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/inventory/offers/${id}`);
  },
  uploadDocument: async (offerId: number, file: File, fileType: DocumentType, fileName?: string): Promise<OfferDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileType', fileType);
    if (fileName) {
      formData.append('fileName', fileName);
    }
    const response = await api.post(`/suppliers/offers/${offerId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  getDocuments: async (offerId: number): Promise<OfferDocument[]> => {
    const response = await api.get(`/suppliers/offers/${offerId}/documents`);
    return response.data;
  },
  deleteDocument: async (documentId: number): Promise<void> => {
    await api.delete(`/suppliers/offers/documents/${documentId}`);
  },
};

// Inventory Stats API
export const inventoryStatsAPI = {
  getStats: async (): Promise<any> => {
    const response = await api.get('/inventory/stats');
    return response.data;
  },
};

export const inventoryLotsAPI = {
  getAll: async (filters?: { search?: string; isActive?: boolean }): Promise<InventoryLot[]> => {
    const response = await api.get('/inventory/lots', { params: filters });
    return response.data;
  },
  getOne: async (id: number): Promise<InventoryLot> => {
    const response = await api.get(`/inventory/lots/${id}`);
    return response.data;
  },
  create: async (data: {
    lotNumber: string;
    supplierId?: number;
    receivedDate?: string;
    expiryDate?: string;
    notes?: string;
  }): Promise<InventoryLot> => {
    const response = await api.post('/inventory/lots', data);
    return response.data;
  },
  update: async (id: number, data: Partial<{
    lotNumber: string;
    supplierId: number | null;
    receivedDate: string | null;
    expiryDate: string | null;
    notes: string | null;
  }>): Promise<InventoryLot> => {
    const response = await api.patch(`/inventory/lots/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/inventory/lots/${id}`);
  },
  addItems: async (
    id: number,
    items: Array<{
      productId: number;
      trayId?: number;
      quantity: number;
      unit?: string;
      status?: InventoryItem['status'];
      expiryDate?: string;
      notes?: string;
    }>,
  ): Promise<InventoryLot> => {
    const response = await api.post(`/inventory/lots/${id}/items`, { items });
    return response.data;
  },
};

export const productStockAPI = {
  getAll: async (filters?: { search?: string; categoryId?: number; isActive?: boolean }): Promise<ProductStockRow[]> => {
    const response = await api.get('/inventory/product-stock', { params: filters });
    return response.data;
  },
  getOne: async (productId: number): Promise<ProductStockRow> => {
    const response = await api.get(`/inventory/product-stock/${productId}`);
    return response.data;
  },
};

// Stock Alerts API
export const stockAlertsAPI = {
  getAll: async (options?: {
    status?: StockAlertStatus;
    productId?: number;
    alertType?: StockAlertType;
    limit?: number;
    offset?: number;
  }): Promise<{ alerts: StockAlert[]; total: number }> => {
    const response = await api.get('/inventory/stock-alerts', { params: options });
    return response.data;
  },

  create: async (data: {
    productId: number;
    alertType: StockAlertType;
    message: string;
    currentQty: number;
    requiredQty?: number;
    dealId?: number;
    threshold?: number;
  }): Promise<StockAlert> => {
    const response = await api.post('/inventory/stock-alerts', data);
    return response.data;
  },

  updateStatus: async (id: number, status: StockAlertStatus, userId?: number): Promise<StockAlert> => {
    const response = await api.patch(`/inventory/stock-alerts/${id}/status`, { status, userId });
    return response.data;
  },

  checkAvailability: async (items: Array<{ productId: number; requiredQuantity: number; unit?: string }>): Promise<{
    available: boolean;
    details: Array<{
      productId: number;
      productName: string;
      sku: string;
      isInventoryLinked: boolean;
      requiredQuantity: number;
      availableQuantity: number;
      status: InventoryAvailabilityStatus;
      requestedUnit?: string | null;
      inventoryUnit?: string | null;
      unitMismatch?: boolean;
      inventoryItems: Array<{
        id: number;
        quantity: number;
        lotId?: number | null;
        lotNumber?: string | null;
        batchNumber: string | null;
        warehouseId?: number;
        warehouseName?: string;
        location: string;
      }>;
    }>;
  }> => {
    const response = await api.post('/inventory/check-availability', { items });
    return response.data;
  },

  getProductSummary: async (productIds: number[]): Promise<Array<{
    productId: number;
    isInventoryLinked: boolean;
    totalQuantity: number;
    availableQuantity: number;
    hasStock: boolean;
    isLowStock: boolean;
    stockStatus: ProductStockStatus;
  }>> => {
    const response = await api.get('/inventory/product-summary', {
      params: { productIds: productIds.join(',') }
    });
    return response.data;
  },
};

// Catalogue Types
export interface Category {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    products: number;
  };
}

export interface CostRange {
  id: number;
  productId: number;
  minQuantity: number;
  maxQuantity?: number;
  unit?: string | null;
  pricePerUnit: string;
  currency: string;
  createdAt: string;
  updatedAt: string;
}

// Catalogue API
export const catalogueAPI = {
  // Categories
  createCategory: async (data: Partial<Category>): Promise<Category> => {
    const response = await api.post('/catalogue/categories', data);
    return response.data;
  },
  getCategories: async (): Promise<Category[]> => {
    const response = await api.get('/catalogue/categories');
    return response.data;
  },
  getCategory: async (id: number): Promise<Category> => {
    const response = await api.get(`/catalogue/categories/${id}`);
    return response.data;
  },
  updateCategory: async (id: number, data: Partial<Category>): Promise<Category> => {
    const response = await api.patch(`/catalogue/categories/${id}`, data);
    return response.data;
  },
  deleteCategory: async (id: number): Promise<void> => {
    await api.delete(`/catalogue/categories/${id}`);
  },

  // Products
  createProduct: async (data: Partial<Product>): Promise<Product> => {
    const response = await api.post('/catalogue/products', data);
    return response.data;
  },
  bulkImportProducts: async (products: any[]): Promise<any> => {
    const response = await api.post('/catalogue/products/bulk-import', { products });
    return response.data;
  },
  getProducts: async (filters?: {
    categoryId?: number;
    supplierId?: number;
    search?: string;
    isActive?: boolean;
  }): Promise<Product[]> => {
    const params = new URLSearchParams();
    if (filters?.categoryId) params.append('categoryId', filters.categoryId.toString());
    if (filters?.supplierId) params.append('supplierId', filters.supplierId.toString());
    if (filters?.search) params.append('search', filters.search);
    if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());

    const response = await api.get(`/catalogue/products?${params.toString()}`);
    return response.data;
  },
  getProduct: async (id: number): Promise<Product> => {
    const response = await api.get(`/catalogue/products/${id}`);
    return response.data;
  },
  getProductPurchaseOrders: async (id: number): Promise<CustomerPurchaseOrder[]> => {
    const response = await api.get(`/catalogue/products/${id}/purchase-orders`);
    return response.data;
  },
  getProductQuoteHistory: async (id: number): Promise<ProductQuoteHistory> => {
    const response = await api.get(`/catalogue/products/${id}/quote-history`);
    return response.data;
  },
  updateProduct: async (id: number, data: Partial<Product>): Promise<Product> => {
    const response = await api.patch(`/catalogue/products/${id}`, data);
    return response.data;
  },
  updateProductInventoryLink: async (id: number, isInventoryLinked: boolean): Promise<Product> => {
    const response = await api.patch(`/catalogue/products/${id}/inventory-link`, { isInventoryLinked });
    return response.data;
  },
  deleteProduct: async (id: number): Promise<void> => {
    await api.delete(`/catalogue/products/${id}`);
  },

  // Cost Ranges
  createCostRange: async (productId: number, data: Partial<CostRange>): Promise<CostRange> => {
    const response = await api.post(`/catalogue/products/${productId}/cost-ranges`, data);
    return response.data;
  },
  getCostRanges: async (productId: number): Promise<CostRange[]> => {
    const response = await api.get(`/catalogue/products/${productId}/cost-ranges`);
    return response.data;
  },
  updateCostRange: async (id: number, data: Partial<CostRange>): Promise<CostRange> => {
    const response = await api.patch(`/catalogue/cost-ranges/${id}`, data);
    return response.data;
  },
  deleteCostRange: async (id: number): Promise<void> => {
    await api.delete(`/catalogue/cost-ranges/${id}`);
  },

  // Documents
  uploadDocument: async (
    productId: number,
    file: File,
    fileType: DocumentType,
    fileName?: string,
    parentDocumentId?: number
  ): Promise<ProductDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileType', fileType);
    if (fileName) formData.append('fileName', fileName);
    if (parentDocumentId !== undefined) {
      formData.append('parentDocumentId', parentDocumentId.toString());
    }

    const response = await api.post(`/catalogue/products/${productId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  getDocuments: async (productId: number): Promise<ProductDocument[]> => {
    const response = await api.get(`/catalogue/products/${productId}/documents`);
    return response.data;
  },
  deleteDocument: async (documentId: number): Promise<void> => {
    await api.delete(`/catalogue/documents/${documentId}`);
  },
  updateDocument: async (
    documentId: number,
    data: { fileName?: string; fileType?: DocumentType; parentDocumentId?: number | null },
  ): Promise<ProductDocument> => {
    const response = await api.patch(`/catalogue/documents/${documentId}`, data);
    return response.data;
  },

  // Custom Properties (Phase 1.2)
  createCustomProperty: async (data: Partial<CustomProperty>): Promise<CustomProperty> => {
    const response = await api.post('/custom-properties', data);
    return response.data;
  },
  getCustomProperties: async (objectType?: string): Promise<CustomProperty[]> => {
    const params = objectType ? `?objectType=${objectType}` : '';
    const response = await api.get(`/custom-properties${params}`);
    return response.data;
  },
  getCustomProperty: async (id: number): Promise<CustomProperty> => {
    const response = await api.get(`/custom-properties/${id}`);
    return response.data;
  },
  updateCustomProperty: async (id: number, data: Partial<CustomProperty>): Promise<CustomProperty> => {
    const response = await api.patch(`/custom-properties/${id}`, data);
    return response.data;
  },
  deleteCustomProperty: async (id: number): Promise<void> => {
    await api.delete(`/custom-properties/${id}`);
  },
  setCustomPropertyValue: async (data: {
    propertyId: number;
    clientId: number;
    value: string;
  }): Promise<CustomPropertyValue> => {
    const response = await api.post('/custom-properties/values', data);
    return response.data;
  },
  getCustomPropertyValues: async (clientId: number): Promise<CustomPropertyValue[]> => {
    const response = await api.get(`/custom-properties/values/client/${clientId}`);
    return response.data;
  },
  deleteCustomPropertyValue: async (propertyId: number, clientId: number): Promise<void> => {
    await api.delete(`/custom-properties/values/${propertyId}/client/${clientId}`);
  },
};

// Phase 2: Activities API
export const activitiesAPI = {
  create: async (data: Partial<Activity>): Promise<Activity> => {
    const response = await api.post('/activities', data);
    return response.data;
  },
  getAll: async (filters?: {
    page?: number;
    limit?: number;
    clientId?: number;
    dealId?: number;
    type?: ActivityType;
    userId?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<{ data: Activity[]; meta: { total: number; page: number; limit: number; totalPages: number } }> => {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.clientId) params.append('clientId', filters.clientId.toString());
    if (filters?.dealId) params.append('dealId', filters.dealId.toString());
    if (filters?.type) params.append('type', filters.type);
    if (filters?.userId) params.append('userId', filters.userId.toString());
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);

    const response = await api.get(`/activities?${params.toString()}`);
    return response.data;
  },
  getById: async (id: number): Promise<Activity> => {
    const response = await api.get(`/activities/${id}`);
    return response.data;
  },
  update: async (id: number, data: Partial<Activity>): Promise<Activity> => {
    const response = await api.patch(`/activities/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/activities/${id}`);
  },
};

// Phase 3: Saved Searches & Segments
export interface SavedSearch {
  id: number;
  name: string;
  description?: string;
  criteria: any; // JSON object with search criteria
  isGlobal: boolean;
  userId: number;
  user?: User;
  createdAt: string;
  updatedAt: string;
}

export interface Segment {
  id: number;
  name: string;
  description?: string;
  type: 'DYNAMIC' | 'STATIC';
  criteria?: any; // For dynamic segments
  color?: string;
  isGlobal: boolean;
  userId: number;
  user?: User;
  _count?: {
    clients: number;
  };
  createdAt: string;
  updatedAt: string;
}

export const savedSearchesAPI = {
  getAll: async (): Promise<SavedSearch[]> => {
    const response = await api.get('/saved-searches');
    return response.data;
  },
  getById: async (id: number): Promise<SavedSearch> => {
    const response = await api.get(`/saved-searches/${id}`);
    return response.data;
  },
  create: async (data: Partial<SavedSearch>): Promise<SavedSearch> => {
    const response = await api.post('/saved-searches', data);
    return response.data;
  },
  update: async (id: number, data: Partial<SavedSearch>): Promise<SavedSearch> => {
    const response = await api.patch(`/saved-searches/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/saved-searches/${id}`);
  },
  execute: async (id: number, page?: number, limit?: number): Promise<PaginatedResponse<Client>> => {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());
    const response = await api.get(`/saved-searches/${id}/execute?${params.toString()}`);
    return response.data;
  },
};

export const segmentsAPI = {
  getAll: async (): Promise<Segment[]> => {
    const response = await api.get('/segments');
    return response.data;
  },
  getById: async (id: number): Promise<Segment> => {
    const response = await api.get(`/segments/${id}`);
    return response.data;
  },
  create: async (data: Partial<Segment> & { clientIds?: number[] }): Promise<Segment> => {
    const response = await api.post('/segments', data);
    return response.data;
  },
  update: async (id: number, data: Partial<Segment>): Promise<Segment> => {
    const response = await api.patch(`/segments/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/segments/${id}`);
  },
  getClients: async (id: number, page?: number, limit?: number): Promise<PaginatedResponse<Client>> => {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());
    const response = await api.get(`/segments/${id}/clients?${params.toString()}`);
    return response.data;
  },
  addClients: async (id: number, clientIds: number[]): Promise<Segment> => {
    const response = await api.post(`/segments/${id}/clients`, { clientIds });
    return response.data;
  },
  removeClients: async (id: number, clientIds: number[]): Promise<Segment> => {
    const response = await api.delete(`/segments/${id}/clients`, { data: { clientIds } });
    return response.data;
  },
};

// ============================================
// Phase 4: Email Integration & Templates
// ============================================

export type EmailStatus = 'DRAFT' | 'QUEUED' | 'SENT' | 'FAILED' | 'SCHEDULED';
export type TemplateCategory = 'WELCOME' | 'FOLLOW_UP' | 'PROPOSAL' | 'THANK_YOU' | 'REMINDER' | 'NEWSLETTER' | 'CUSTOM';

export interface EmailTemplate {
  id: number;
  name: string;
  subject: string;
  body: string;
  category: TemplateCategory;
  isActive: boolean;
  variables: string[];
  description?: string;
  createdById: number;
  createdBy?: User;
  createdAt: string;
  updatedAt: string;
  _count?: {
    emailHistory: number;
  };
}

export interface EmailHistory {
  id: number;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  body: string;
  status: EmailStatus;
  error?: string;
  scheduledFor?: string;
  sentAt?: string;
  templateId?: number;
  template?: {
    id: number;
    name: string;
    category: TemplateCategory;
  };
  clientId?: number;
  client?: {
    id: number;
    name: string;
    email?: string;
  };
  sentById: number;
  sentBy?: User;
  createdAt: string;
  updatedAt: string;
}

export interface SendEmailDto {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  body: string;
  templateId?: number;
  clientId?: number;
  scheduledFor?: string;
  templateVariables?: Record<string, any>;
}

export interface EmailStatistics {
  total: number;
  sent: number;
  failed: number;
  scheduled: number;
  queued: number;
  draft: number;
}

export interface TemplateVariables {
  client: string[];
  deal: string[];
  user: string[];
  system: string[];
}

export const emailTemplatesAPI = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    category?: TemplateCategory;
    isActive?: boolean;
    search?: string;
  }): Promise<PaginatedResponse<EmailTemplate>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.category) queryParams.append('category', params.category);
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params?.search) queryParams.append('search', params.search);

    const response = await api.get(`/email-templates?${queryParams.toString()}`);
    return response.data;
  },
  getById: async (id: number): Promise<EmailTemplate> => {
    const response = await api.get(`/email-templates/${id}`);
    return response.data;
  },
  create: async (data: Partial<EmailTemplate>): Promise<EmailTemplate> => {
    const response = await api.post('/email-templates', data);
    return response.data;
  },
  update: async (id: number, data: Partial<EmailTemplate>): Promise<EmailTemplate> => {
    const response = await api.patch(`/email-templates/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/email-templates/${id}`);
  },
  duplicate: async (id: number): Promise<EmailTemplate> => {
    const response = await api.post(`/email-templates/${id}/duplicate`);
    return response.data;
  },
  getVariables: async (): Promise<TemplateVariables> => {
    const response = await api.get('/email-templates/variables');
    return response.data;
  },
};

export const emailHistoryAPI = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    status?: EmailStatus;
    clientId?: number;
    userId?: number;
    search?: string;
  }): Promise<PaginatedResponse<EmailHistory>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.clientId) queryParams.append('clientId', params.clientId.toString());
    if (params?.userId) queryParams.append('userId', params.userId.toString());
    if (params?.search) queryParams.append('search', params.search);

    const response = await api.get(`/email-history?${queryParams.toString()}`);
    return response.data;
  },
  getById: async (id: number): Promise<EmailHistory> => {
    const response = await api.get(`/email-history/${id}`);
    return response.data;
  },
  create: async (data: Partial<EmailHistory>): Promise<EmailHistory> => {
    const response = await api.post('/email-history', data);
    return response.data;
  },
  update: async (id: number, data: Partial<EmailHistory>): Promise<EmailHistory> => {
    const response = await api.patch(`/email-history/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/email-history/${id}`);
  },
  send: async (data: SendEmailDto): Promise<EmailHistory> => {
    const response = await api.post('/email-history/send', data);
    return response.data;
  },
  getStatistics: async (userId?: number): Promise<EmailStatistics> => {
    const queryParams = userId ? `?userId=${userId}` : '';
    const response = await api.get(`/email-history/statistics${queryParams}`);
    return response.data;
  },
};

export const samplesAPI = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: SampleStatus;
    statuses?: SampleStatus[];
    clientId?: number;
    supplierId?: number;
  }): Promise<PaginatedResponse<Sample>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.statuses?.length) queryParams.append('statuses', params.statuses.join(','));
    if (params?.clientId) queryParams.append('clientId', params.clientId.toString());
    if (params?.supplierId) queryParams.append('supplierId', params.supplierId.toString());

    const response = await api.get(`/samples?${queryParams.toString()}`);
    return response.data;
  },

  getById: async (id: number): Promise<Sample> => {
    const response = await api.get(`/samples/${id}`);
    return response.data;
  },

  create: async (data: Partial<Sample>): Promise<Sample> => {
    const response = await api.post('/samples', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Sample>): Promise<Sample> => {
    const response = await api.patch(`/samples/${id}`, data);
    return response.data;
  },

  updateStatus: async (id: number, data: {
    status: SampleStatus;
    notes?: string;
    actualDeliveryDate?: string;
    deliveryRecipient?: string;
  }): Promise<Sample> => {
    const response = await api.patch(`/samples/${id}/status`, data);
    return response.data;
  },

  ship: async (id: number, data: {
    trackingNumber: string;
    lotNumber: string;
    shippingCarrier: string;
    shippingMethod?: string;
    shippingDate?: string;
    estimatedDeliveryDate?: string;
  }): Promise<Sample> => {
    const response = await api.post(`/samples/${id}/ship`, data);
    return response.data;
  },

  addFeedback: async (id: number, data: {
    clientFeedback: string;
    clientRating?: number;
    clientDecision?: string;
  }): Promise<Sample> => {
    const response = await api.post(`/samples/${id}/feedback`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/samples/${id}`);
  },

  getByClient: async (clientId: number): Promise<Sample[]> => {
    const response = await api.get(`/clients/${clientId}/samples`);
    return response.data;
  },

  getAnalytics: async (): Promise<SampleAnalytics> => {
    const response = await api.get('/samples/analytics/overview');
    return response.data;
  },
};

export const blacklistAPI = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    availability?: 'AVAILABLE' | 'PROTECTED' | 'PERMANENT';
  }): Promise<PaginatedResponse<BlacklistEntry>> => {
    const response = await api.get('/blacklist', { params });
    return response.data;
  },
  requestClaim: async (clientId: number): Promise<ClientClaim> => {
    const response = await api.post(`/blacklist/${clientId}/claims`);
    return response.data;
  },
  getMyClaims: async (): Promise<ClientClaim[]> => {
    const response = await api.get('/blacklist/claims/mine');
    return response.data;
  },
  getAdminClaims: async (status: ClientClaim['status'] = 'PENDING'): Promise<ClientClaim[]> => {
    const response = await api.get('/blacklist/admin/claims', { params: { status } });
    return response.data;
  },
  reviewClaim: async (
    id: number,
    decision: 'APPROVED' | 'REJECTED',
    reviewNote?: string,
  ): Promise<ClientClaim> => {
    const response = await api.patch(`/blacklist/admin/claims/${id}`, { decision, reviewNote });
    return response.data;
  },
  getPermanent: async (search?: string): Promise<PermanentBlacklistEntry[]> => {
    const response = await api.get('/blacklist/admin/permanent', { params: search ? { search } : undefined });
    return response.data;
  },
  protectClient: async (clientId: number, reason?: string): Promise<PermanentBlacklistEntry> => {
    const response = await api.put(`/blacklist/admin/permanent/${clientId}`, { reason });
    return response.data;
  },
  unprotectClient: async (clientId: number): Promise<{ id: number; name: string; isPermanentlyProtected: false }> => {
    const response = await api.delete(`/blacklist/admin/permanent/${clientId}`);
    return response.data;
  },
};

export const formulasAPI = {
  getAll: async (search?: string): Promise<Formula[]> => {
    const response = await api.get('/formulas', { params: search ? { search } : undefined });
    return response.data;
  },
  getById: async (id: number): Promise<Formula> => {
    const response = await api.get(`/formulas/${id}`);
    return response.data;
  },
  create: async (data: FormulaInput): Promise<Formula> => {
    const response = await api.post('/formulas', data);
    return response.data;
  },
  update: async (id: number, data: Partial<FormulaInput>): Promise<Formula> => {
    const response = await api.patch(`/formulas/${id}`, data);
    return response.data;
  },
  duplicate: async (id: number): Promise<Formula> => {
    const response = await api.post(`/formulas/${id}/duplicate`);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/formulas/${id}`);
  },
};

export interface SampleAnalytics {
  overview: {
    totalSamples: number;
    approvedSamples: number;
    rejectedSamples: number;
    underReviewSamples: number;
    convertedToDeals: number;
    conversionRate: number;
    dealConversionRate: number;
    samplesLast30Days: number;
    samplesLast7Days: number;
  };
  costs: {
    totalSampleCost: number;
    totalShippingCost: number;
    totalCost: number;
    averageCostPerSample: number;
  };
  revenue: {
    totalRevenue: number;
    roi: number;
  };
  feedback: {
    samplesWithFeedback: number;
    averageRating: number;
  };
  breakdowns: {
    status: Record<string, number>;
    type: Record<string, number>;
    decision: Record<string, number>;
  };
  topPerformers: {
    products: Array<{
      productId: number;
      productName: string;
      totalSamples: number;
      approved: number;
      rejected: number;
      converted: number;
    }>;
    clients: Array<{
      clientId: number;
      clientName: string;
      totalSamples: number;
      approved: number;
      rejected: number;
      converted: number;
    }>;
  };
  recentSamples: Array<{
    id: number;
    trackingNumber: string;
    clientName: string;
    productName?: string;
    status: SampleStatus;
    type: SampleType;
    createdAt: string;
  }>;
}

// ============================================
// Phase 1: Pipeline & Forecasting APIs
// ============================================

export const pipelineAPI = {
  // Pipeline CRUD
  getAll: async (): Promise<Pipeline[]> => {
    const response = await api.get('/pipelines');
    return response.data;
  },

  getById: async (id: number): Promise<Pipeline> => {
    const response = await api.get(`/pipelines/${id}`);
    return response.data;
  },

  create: async (data: Partial<Pipeline>): Promise<Pipeline> => {
    const response = await api.post('/pipelines', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Pipeline>): Promise<Pipeline> => {
    const response = await api.patch(`/pipelines/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/pipelines/${id}`);
  },

  // Pipeline Stage CRUD
  getStages: async (pipelineId: number): Promise<PipelineStage[]> => {
    const response = await api.get(`/pipelines/${pipelineId}/stages`);
    return response.data;
  },

  createStage: async (pipelineId: number, data: Partial<PipelineStage>): Promise<PipelineStage> => {
    const response = await api.post(`/pipelines/${pipelineId}/stages`, data);
    return response.data;
  },

  updateStage: async (stageId: number, data: Partial<PipelineStage>): Promise<PipelineStage> => {
    const response = await api.patch(`/pipelines/stages/${stageId}`, data);
    return response.data;
  },

  deleteStage: async (stageId: number): Promise<void> => {
    await api.delete(`/pipelines/stages/${stageId}`);
  },

  // Deal Stage Management
  moveDealStage: async (dealId: number, data: {
    toStageId: number;
    probability?: number;
    notes?: string;
  }): Promise<Deal> => {
    const response = await api.patch(`/pipelines/deals/${dealId}/stage`, data);
    return response.data;
  },

  getDealStageHistory: async (dealId: number): Promise<DealStageHistory[]> => {
    const response = await api.get(`/pipelines/deals/${dealId}/stage-history`);
    return response.data;
  },

  // Pipeline Board View
  getBoardView: async (pipelineId?: number): Promise<PipelineBoardData> => {
    const url = pipelineId
      ? `/pipelines/board/view?pipelineId=${pipelineId}`
      : '/pipelines/board/view';
    const response = await api.get(url);
    return response.data;
  },
};

export const forecastingAPI = {
  getPipelineForecast: async (params?: {
    pipelineId?: number;
    userId?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<ForecastData> => {
    const queryParams = new URLSearchParams();
    if (params?.pipelineId) queryParams.append('pipelineId', params.pipelineId.toString());
    if (params?.userId) queryParams.append('userId', params.userId.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const response = await api.get(`/forecasting/pipeline?${queryParams.toString()}`);
    return response.data;
  },

  getUserForecast: async (userId: number, params?: {
    pipelineId?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<ForecastData> => {
    const queryParams = new URLSearchParams();
    if (params?.pipelineId) queryParams.append('pipelineId', params.pipelineId.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const response = await api.get(`/forecasting/user/${userId}?${queryParams.toString()}`);
    return response.data;
  },

  getTeamForecast: async (params?: {
    pipelineId?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<ForecastData & { userForecasts: any[] }> => {
    const queryParams = new URLSearchParams();
    if (params?.pipelineId) queryParams.append('pipelineId', params.pipelineId.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const response = await api.get(`/forecasting/team?${queryParams.toString()}`);
    return response.data;
  },
};

// Picking Tasks API
export const pickingTasksAPI = {
  // Generate a picking task for a deal
  generate: async (dealId: number, warehouseId?: number): Promise<PickingTask> => {
    const url = warehouseId
      ? `/picking-tasks/generate/${dealId}?warehouseId=${warehouseId}`
      : `/picking-tasks/generate/${dealId}`;
    const response = await api.post(url);
    return response.data;
  },

  // Get all picking tasks with optional filters
  getAll: async (params?: {
    status?: PickingTaskStatus;
    assignedUserId?: number;
    warehouseId?: number;
    dealId?: number;
  }): Promise<PickingTask[]> => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.assignedUserId) queryParams.append('assignedUserId', params.assignedUserId.toString());
    if (params?.warehouseId) queryParams.append('warehouseId', params.warehouseId.toString());
    if (params?.dealId) queryParams.append('dealId', params.dealId.toString());
    const response = await api.get(`/picking-tasks?${queryParams.toString()}`);
    return response.data;
  },

  // Get a single picking task
  getById: async (id: number): Promise<PickingTask> => {
    const response = await api.get(`/picking-tasks/${id}`);
    return response.data;
  },

  // Assign a user to a picking task
  assignUser: async (taskId: number, userId: number): Promise<PickingTask> => {
    const response = await api.patch(`/picking-tasks/${taskId}/assign/${userId}`);
    return response.data;
  },

  // Start picking
  startPicking: async (taskId: number): Promise<PickingTask> => {
    const response = await api.post(`/picking-tasks/${taskId}/start-picking`);
    return response.data;
  },

  // Confirm an item has been picked
  confirmItemPicked: async (
    taskId: number,
    itemId: number,
    data: { pickedQuantity: number; scannedBatchCode?: string }
  ): Promise<PickingTask> => {
    const response = await api.patch(`/picking-tasks/${taskId}/items/${itemId}/confirm`, data);
    return response.data;
  },

  // Complete picking
  completePicking: async (taskId: number): Promise<PickingTask> => {
    const response = await api.post(`/picking-tasks/${taskId}/complete-picking`);
    return response.data;
  },

  // Start QC process
  startQC: async (taskId: number): Promise<PickingTask> => {
    const response = await api.post(`/picking-tasks/${taskId}/start-qc`);
    return response.data;
  },

  // Submit QC checklist
  submitQCChecklist: async (data: {
    pickingTaskId: number;
    checks: { checkType: QCCheckType; passed: boolean; notes?: string }[];
    overallNotes?: string;
  }): Promise<PickingTask> => {
    const response = await api.post('/picking-tasks/qc-checklist', data);
    return response.data;
  },

  // Complete packing
  completePacking: async (data: {
    pickingTaskId: number;
    totalWeight?: number;
    totalPackages?: number;
    packagingNotes?: string;
  }): Promise<PickingTask> => {
    const response = await api.post('/picking-tasks/complete-packing', data);
    return response.data;
  },

  // Get packing slip data
  getPackingSlipData: async (taskId: number): Promise<PackingSlipData> => {
    const response = await api.get(`/picking-tasks/${taskId}/packing-slip`);
    return response.data;
  },

  // Cancel a picking task
  cancel: async (taskId: number, reason?: string): Promise<PickingTask> => {
    const response = await api.delete(`/picking-tasks/${taskId}`, { data: { reason } });
    return response.data;
  },
};

// ========================================
// Purchase Orders
// ========================================

export type PurchaseOrderStatus = 'CREATED' | 'SENT' | 'CONFIRMED' | 'RECEIVED';

export interface PurchaseOrderItem {
  id: number;
  purchaseOrderId: number;
  productId?: number;
  description: string;
  quantity: number;
  unitPrice: string;
  totalPrice: string;
  product?: { id: number; name: string; sku: string; unit?: string };
}

export interface PurchaseOrderDocument {
  id: number;
  purchaseOrderId: number;
  documentType: DocumentType;
  name: string;
  description?: string;
  filePath: string;
  fileSize?: number;
  mimeType?: string;
  uploadedAt: string;
  uploadedBy?: number;
  isArchived: boolean;
  archivedAt?: string;
}

export interface PurchaseOrder {
  id: number;
  poNumber: string;
  supplierId: number;
  companyId: number;
  status: PurchaseOrderStatus;
  expectedDeliveryDate?: string;
  totalAmount: string;
  currency: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  supplier?: Supplier;
  company?: { id: number; name: string; address?: string; city?: string; state?: string; postalCode?: string; country?: string; phone?: string; email?: string; taxId?: string; registrationNo?: string };
  items?: PurchaseOrderItem[];
  documents?: PurchaseOrderDocument[];
  _count?: { items: number; documents?: number };
}

export const purchaseOrdersAPI = {
  getAll: async (filters?: {
    page?: number;
    limit?: number;
    status?: string;
    supplierId?: number;
    search?: string;
  }): Promise<any> => {
    const response = await api.get('/purchase-orders', { params: filters });
    return response.data;
  },

  getById: async (id: number): Promise<PurchaseOrder> => {
    const response = await api.get(`/purchase-orders/${id}`);
    return response.data;
  },

  create: async (data: any): Promise<PurchaseOrder> => {
    const response = await api.post('/purchase-orders', data);
    return response.data;
  },

  update: async (id: number, data: any): Promise<PurchaseOrder> => {
    const response = await api.patch(`/purchase-orders/${id}`, data);
    return response.data;
  },

  updateStatus: async (id: number, status: string): Promise<PurchaseOrder> => {
    const response = await api.patch(`/purchase-orders/${id}/status`, { status });
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/purchase-orders/${id}`);
  },

  downloadPdf: async (id: number): Promise<Blob> => {
    const response = await api.get(`/purchase-orders/${id}/pdf`, { responseType: 'blob' });
    return response.data;
  },

  uploadDocument: async (
    id: number,
    file: File,
    options?: { documentType?: DocumentType; name?: string; description?: string },
  ): Promise<PurchaseOrderDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', options?.documentType || 'OTHER');
    if (options?.name) formData.append('name', options.name);
    if (options?.description) formData.append('description', options.description);

    const response = await api.post(`/purchase-orders/${id}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getDocuments: async (id: number): Promise<PurchaseOrderDocument[]> => {
    const response = await api.get(`/purchase-orders/${id}/documents`);
    return response.data;
  },

  downloadDocument: async (id: number, documentId: number, fileName: string): Promise<void> => {
    const response = await api.get(`/purchase-orders/${id}/documents/${documentId}/download`, {
      responseType: 'blob',
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  deleteDocument: async (id: number, documentId: number): Promise<void> => {
    await api.delete(`/purchase-orders/${id}/documents/${documentId}`);
  },
};

// Note Templates
export interface NoteTemplate {
  id: number;
  name: string;
  content: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export const noteTemplatesAPI = {
  getAll: async (): Promise<NoteTemplate[]> => {
    const response = await api.get('/purchase-orders/note-templates');
    return response.data;
  },
  getById: async (id: number): Promise<NoteTemplate> => {
    const response = await api.get(`/purchase-orders/note-templates/${id}`);
    return response.data;
  },
  create: async (data: { name: string; content: string; isDefault?: boolean }): Promise<NoteTemplate> => {
    const response = await api.post('/purchase-orders/note-templates', data);
    return response.data;
  },
  update: async (id: number, data: { name?: string; content?: string; isDefault?: boolean }): Promise<NoteTemplate> => {
    const response = await api.patch(`/purchase-orders/note-templates/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/purchase-orders/note-templates/${id}`);
  },
};

export default api;
